import { LocalStorageKeys } from '@/constants/LocalStorageKeys';
import CompAddressUtils from '@/models/comp/CompAddressUtils';
import ICompAddress from '@/models/CompAddress';
import IPublicRecord from '@/models/public-records/IPublicRecord';
import PublicRecordsRequest from '@/models/requests/PublicRecordsRequest';
import WebEnvConfig from '@/models/WebEnvConfig';
import theLocalStorageUtils from '@/utils/LocalStorageUtils';
import ServiceHelperFactory from './ServiceHelperFactory';
import BaseApiService from '../BaseApiService';
import IServiceHelper from './IServiceHelper';
import _FakePayload from './_FakeCompPayload';
import { SourceDataInitials } from '@/constants/SourceDataInitials';
import { isFakeDataHostnameAllowed } from '@/constants/FakeDataHostnames';
import IMlsImportResult36 from '@/models/mls/IMlsImportResult36';


/**
 * Service for converting MLS simple shape to the standard 3.6 shape.
 */
export default class MLSService extends BaseApiService {
    private readonly currentHostname: string;

    constructor(webEnvConfig: WebEnvConfig) {
        super(webEnvConfig.urls?.quickSourceApi ?? '');
        this.currentHostname = String(webEnvConfig.hostname || '').toLowerCase();
    }

    getServiceHelper(): IServiceHelper {
        return ServiceHelperFactory.getHelper();
    }

    /**
     * Get the raw MLS data (an array of flat field-name/value maps, one per property) from the
     * ConvertMLSMapperDataToUAD36 endpoint. Each item still needs CompsManager.transformSourceDataPayload()
     * to build the final IFeedData (address + flattened sourceData) shape.
     * @param data
     * @returns an array of raw flat field maps or null on not found or error
     */
    async getMLSData(data: IMlsImportResult36): Promise<any[] | null> {
        this.addContentTypeJsonHeader();
        this.addAcceptJsonHeader();

        try {
            const response = await this.axiosInstance.post('v7/DataDiscrepancy/ConvertMLSMapperDataToUAD36', data);
            if (response.data) {
                if (response.status === 200) {
                    const result: any[] = response.data;
                    return result;
                }
            } else {
                throw new Error(`MLSService.getMLSData(), No data returned from endpoint for MLS data`);
            }

            //return loadFakeData() as IFeedDatas;
            
        } catch (error) {
            const errorDescription = this.getErrorDescription(error);
            console.log(`MLSService.getMLSData(), Error[${errorDescription}]`);
        }

        return null;
    }

}

function loadFakeData() {
    return [
  {
      "COMP_PHOTO": "",
      "AD_ADDR1": "84 Appletree Ln",
      "DB_UNITNO": "",
      "DB_CITY": "Sewell",
      "DB_STATE": "NJ",
      "DB_ZIP": "8080",
      "SC_SOURCE1": "Other",
      "PU_DATA_SOURCE": "Other",
      "PU_DATA_SOURCE_MLS_NUM": "",
      "PU_DATA_SOURCE_ASSESSOR_ID": "",
      "PROX1": "",
      "PU_DISTANCE": "",
      "PU_MILES": "",
      "PU_KILOMETERS": "",
      "PU_DIRECTION": "",
      "LP_PRICE": "475000",
      "LP_STATUS": "",
      "CONT_PRICE": "",
      "PU_CONT_PRICE_KNOWN": "",
      "PU_CONT_PRICE": "",
      "PR_SALEPR1": "",
      "DP_FINTYP1": "",
      "EX_CONCAMT": "",
      "PU_CONC_PRESENT": "",
      "PU_CONC_AMOUNT": "",
      "TRANSFER_TERMS": "",
      "CONT_DATE": "",
      "PU_CONT_DATE_KNOWN": "",
      "PU_CONT_DATE": "",
      "DT_SALTIM1": "",
      "EX_DOM": "1",
      "SP_TO_LP_RATIO": "",
      "ATT_DET": "false",
      "PROP_APPRAISER_RIGHTS": "Fee Simple",
      "ANNUAL_GRND_RENT": "",
      "NATIVE_AM_LANDS": "",
      "PU_NATIVE_AM_LANDS_YES": "",
      "PU_NATIVE_AM_LANDS_NO": "",
      "PU_NATIVE_AM_LANDS_TYPE": "",
      "ALL_RIGHTS_INCLUDED": "",
      "RIGHTS_NOT_INCLUDED": "",
      "SAME_BLDR_AS_SUBJECT": "",
      "GI_BLANK": "",
      "PU_PROJECT_NAME": "TWIN PONDS EAST",
      "PROJ_NAME": "TWIN PONDS EAST",
      "PU_SAME_PROJECT_AS_SUBJECT": "",
      "PROJ_MONTHLY_FEE": "",
      "PROJ_AMENITIES": "",
      "PROJ_SPECIAL_ASSESSMENTS": "",
      "SITE_PROJECT_TYPE": "",
      "SITE_OWNED_IN_COMMON": "",
      "ST_SITSIZE": "10800 sf",
      "MARKETAREA": "Twin Ponds East",
      "SITE_ZONE_COMPLIANCE": "",
      "SITE_HAZARD_ZONE": "",
      "SITE_PRIMARY_ACCESS": "",
      "SiteStreetType": [
        {
          "SITE_STREET_TYPE": "",
          "PU_SITE_STREET_TYPE": "",
          "PU_SITE_STREET_SURFACE": ""
        }
      ],
      "SITE_PROP_RESTRICT": "",
      "SITE_EASEMENT": "",
      "SITE_TOPOGRAPHY": "",
      "SiteDrainage": [
        {
          "SITE_DRAINAGE": ""
        }
      ],
      "SITE_CHARACTERISTICS": "",
      "SITE_INFLUENCE": "",
      "PU_SITE_INFLUENCE": "",
      "PU_SITE_INFLUENCE_WATER_TYPE": "",
      "PU_SITE_INFLUENCE_PRIVATE_ACCESS": "",
      "SITE_ENV_COND": "",
      "SiteViews": [
        {
          "ST_VIEW": "",
          "PU_ST_VIEW": "",
          "PU_ST_RANGE": ""
        }
      ],
      "SITE_BLANK": "",
      "Dwellings": [
        {
          "YR_BLT": "38",
          "PU_YR_BLT": "38",
          "PU_YR_BLT_EST": "",
          "DWELL_DESIGN": "",
          "PU_STRUCTURE_DESIGN": "true",
          "SF_GLA": "",
          "SF_FINISHED_AREA": "",
          "DWELL_TH_BACKTOBACK": "",
          "PU_DWELL_TH_BACKTOBACK": "",
          "DWELL_TH_LOC": "",
          "PU_DWELL_TH_LOC": "",
          "DWELL_CONST_METHOD": "",
          "PU_DWELL_CONST_METHOD": "",
          "DWELL_MANFCT_HOME_WIDTH": "",
          "PU_DWELL_MANFCT_HOME_WIDTH": "",
          "DWELL_STYLE": "",
          "PU_DWELL_STYLE": "",
          "DWELL_TOTAL_VOLUME": "",
          "DWELL_WINDOW_AREA": "",
          "DWELL_FUNC_ISSUES": "",
          "PU_DWELL_FUNC_ISSUES": "",
          "DWELL_DISASTER_MIT": "",
          "PU_DWELL_DISASTER_MIT": "",
          "Heating": [
            {
              "DWELL_HEAT": "",
              "PU_DWELL_HEAT_SYSTEM": "",
              "PU_DWELL_HEAT_DETAIL": ""
            }
          ],
          "Cooling": [
            {
              "DWELL_COOL": "",
              "PU_DWELL_COOL": ""
            }
          ],
          "DWELL_OTHR1_BLANK": "",
          "Units": [
            {
              "UNIT_ID": "",
              "UNITS_IS_AN_ADU": "",
              "LOC_ADU": "",
              "PU_LOC_ADU_TYPE": "",
              "PU_LOC_ADU_DESC": "",
              "FLOOR_NUM": "",
              "CORNER_UNIT": "",
              "LEVELS": "",
              "BEDS": "4",
              "BATHS": "",
              "PU_FULL_BATHS": "2",
              "PU_HALF_BATHS": "0",
              "UNITS_SF_GLA": "",
              "UNITS_SF_GLA_NONSTD": "",
              "UNITS_SF_GLA_UNFIN": "",
              "UNITS_SF_GLA_BELOW_GRD": "",
              "UNITS_SF_GLA_BELOW_GRD_NONSTD": "",
              "UNITS_SF_GLA_BELOW_GRD_UNFIN": "",
              "DISABILITY_FEATURES": "",
              "UNITS_BLANK": ""
            }
          ]
        }
      ],
      "WaterFrontages": [
        {
          "WATER_FRONTAGE": "",
          "PU_WATER_FRONTAGE_TYPE": "",
          "PU_WATER_FRONTAGE_NAME": "",
          "PU_WATER_FRONTAGE_ACCESS_DEPTH": ""
        }
      ],
      "WATER_FRONTAGE_FEAT": "",
      "WATER_RIGHT_BLD": "",
      "WATER_LINEAR_MEASURE": "",
      "GREEN_RENEW_ENERGY_ONENT": "",
      "BuildingCertifications": [
        {
          "GREEN_BUILD_CERT": "",
          "PU_GREEN_BUILD_CERT_DESC": "",
          "PU_GREEN_BUILD_CERT_RATE": ""
        }
      ],
      "EfficiencyRatings": [
        {
          "GREEN_EFF_RATING": "",
          "PU_GREEN_EFF_RATING": "",
          "PU_GREEN_EFF_SCORE": ""
        }
      ],
      "ExteriorQualities": [
        {
          "EXT_QUAL_STRUCTURE_ID": "",
          "EXT_QUAL_QUALITY": "",
          "EXT_QUAL_EXT_WALLS_TRIM": "",
          "EXT_QUAL_FOUNDATION": "",
          "EXT_QUAL_ROOF": "",
          "EXT_QUAL_WINDOWS": "",
          "BLExteriorQualities": [
            {
              "EXT_QUAL_BLANK_LABEL": "",
              "EXT_QUAL_BLANK": ""
            }
          ]
        }
      ],
      "ExteriorConditions": [
        {
          "EXT_COND_CONDITION": "",
          "EXT_COND_WALLS_TRIM": "",
          "EXT_COND_FOUNDATION": "",
          "EXT_COND_ROOF": "",
          "PU_EXT_COND_ROOF_OBSERVABLE": "",
          "PU_EXT_COND_ROOF_STATUS": "",
          "EXT_COND_WINDOWS": "",
          "BLExteriorConditions": [
            {
              "EXT_COND_BLANK_LABEL": "",
              "EXT_COND_BLANK": ""
            }
          ]
        }
      ],
      "InteriorQualities": [
        {
          "INT_QUAL_STRUCTURE_ID": "",
          "INT_QUAL_QUALITY": "",
          "INT_QUAL_KITCHEN": "",
          "INT_QUAL_BATHS": "",
          "INT_QUAL_FLOORS": "",
          "INT_QUAL_WALLS_CEILINGS": "",
          "BLInteriorQualities": [
            {
              "INT_QUAL_BLANK_LABEL": "",
              "INT_QUAL_BLANK": ""
            }
          ]
        }
      ],
      "InteriorConditions": [
        {
          "INT_COND_CONDITION": "",
          "INT_COND_KITCHEN": "",
          "INT_COND_BATHS": "",
          "INT_COND_FLOORS": "",
          "INT_COND_WALLS_CEILINGS": "",
          "BLInteriorConditions": [
            {
              "INT_COND_BLANK_LABEL": "",
              "INT_COND_BLANK": ""
            }
          ]
        }
      ],
      "ADUQualities": [
        {
          "ADU_LOCATION": "",
          "ADU_QUAL_QUALITY": "",
          "ADU_QUAL_KITCHEN": "",
          "INT_ADU_COND_BATHS": "",
          "ADU_QUAL_FLOORS": "",
          "ADU_QUAL_WALLS_CEILINGS": "",
          "BLADUQualities": [
            {
              "ADU_QUAL_BLANK_LABEL": "",
              "ADU_QUAL_BLANK": ""
            }
          ]
        }
      ],
      "ADUConditions": [
        {
          "ADU_COND_CONDITION": "",
          "ADU_COND_KITCHEN": "",
          "ADU_COND_BATHS": "",
          "ADU_COND_FLOORS": "",
          "ADU_COND_WALLS_CEILINGS": "",
          "BLADUConditions": [
            {
              "ADU_COND_BLANK_LABEL": "",
              "ADU_COND_BLANK": ""
            }
          ]
        }
      ],
      "QUALITY": "",
      "CONDITION": "",
      "Amenities": [
        {
          "OutdoorAccessories": [
            {
              "AMENITY_OUTDOOR_ACCESSORIES": "",
              "PU_AMENITY_OUTDOOR_ACCESSORIES": "",
              "PU_AMENITY_OUTDOOR_ACCESSORIES_NUM": ""
            }
          ],
          "OutdoorLiving": [
            {
              "AMENITY_OUTDOOR_LIVING": "",
              "PU_AMENITY_OUTDOOR_LIVING": ""
            }
          ],
          "WaterFeatures": [
            {
              "AMENITY_WATER_FEATURES": "",
              "PU_AMENITY_WATER_FEATURE": "",
              "PU_AMENITY_WATER_FEATURE_DETAIL": "",
              "PU_AMENITY_WATER_FEATURE_NUM": ""
            }
          ],
          "WholeHome": [
            {
              "AMENITY_WHOLE_HOME": "",
              "PU_AMENITY_WHOLE_HOME": "",
              "PU_AMENITY_WHOLE_HOME_NUM": ""
            }
          ],
          "Miscellaneous": [
            {
              "AMENITY_MISC": "",
              "PU_AMENITY_MISC": "",
              "PU_AMENITY_MISC_NUM": ""
            }
          ]
        }
      ],
      "VehicleStorage": [
        {
          "VEHICLE_STORAGE": "",
          "PU_VEHICLE_STORAGE": "",
          "PU_VEHICLE_STORAGE_SPACES": "",
          "PU_VEHICLE_STORAGE_DETAILS": "",
          "PU_VEHICLE_STORAGE_SQFT": "",
          "BL_VEHICLE_STORAGE": ""
        }
      ],
      "Outbuildings": [
        {
          "OUTBUILDING_TYPE": "",
          "OUTBUILDING_REAL_PROPERTY": "",
          "OUTBUILDING_BUILDING_AREA": "",
          "OUTBUILDING_FINISHED_AREA": "",
          "OUTBUILDING_UNFINISHED_AREA": "",
          "OUTBUILDING_STRUCTURE_VOLUME": "",
          "OUTBUILDING_BATHS_FULL": "",
          "OUTBUILDING_BATHS_HALF": "",
          "OUTBUILDING_KITCHENS": "",
          "OUTBUILDING_HEATING": "",
          "OUTBUILDING_COOLING": "",
          "OutbuildingUtilities": [
            {
              "OUTBUILDING_UTILITIES": ""
            }
          ],
          "SubjectBLOutbuilding": [
            {
              "BL_OUTBUILDING_BLANK": "",
              "BL_OUTBUILDING": ""
            }
          ]
        }
      ],
      "TransferHistories": [
        {
          "TRANSFER_HISTORY_PRESENT": "",
          "TRANSFER_HISTORY_TERMS": "",
          "TRANSFER_HISTORY_DATE": "",
          "TRANSFER_HISTORY_AMOUNT": "",
          "DFE_TRANSFER_HISTORY_AMOUNT_VALUE": "",
          "DFE_TRANSFER_HISTORY_AMOUNT_REASON": "",
          "TRANSFER_HISTORY_SOURCE": ""
        }
      ]
  },
  {
      "COMP_PHOTO": "",
      "AD_ADDR1": "5718 Haller Pl",
      "DB_UNITNO": "",
      "DB_CITY": "Frederick",
      "DB_STATE": "MD",
      "DB_ZIP": "21704",
      "SC_SOURCE1": "Other",
      "PU_DATA_SOURCE": "Other",
      "PU_DATA_SOURCE_MLS_NUM": "",
      "PU_DATA_SOURCE_ASSESSOR_ID": "",
      "PROX1": "",
      "PU_DISTANCE": "",
      "PU_MILES": "",
      "PU_KILOMETERS": "",
      "PU_DIRECTION": "",
      "LP_PRICE": "767890",
      "LP_STATUS": "",
      "CONT_PRICE": "",
      "PU_CONT_PRICE_KNOWN": "",
      "PU_CONT_PRICE": "",
      "PR_SALEPR1": "",
      "DP_FINTYP1": "",
      "EX_CONCAMT": "",
      "PU_CONC_PRESENT": "",
      "PU_CONC_AMOUNT": "",
      "TRANSFER_TERMS": "",
      "CONT_DATE": "",
      "PU_CONT_DATE_KNOWN": "",
      "PU_CONT_DATE": "",
      "DT_SALTIM1": "",
      "EX_DOM": "6",
      "SP_TO_LP_RATIO": "",
      "ATT_DET": "false",
      "PROP_APPRAISER_RIGHTS": "Fee Simple",
      "ANNUAL_GRND_RENT": "",
      "NATIVE_AM_LANDS": "",
      "PU_NATIVE_AM_LANDS_YES": "",
      "PU_NATIVE_AM_LANDS_NO": "",
      "PU_NATIVE_AM_LANDS_TYPE": "",
      "ALL_RIGHTS_INCLUDED": "",
      "RIGHTS_NOT_INCLUDED": "",
      "SAME_BLDR_AS_SUBJECT": "",
      "GI_BLANK": "",
      "PU_PROJECT_NAME": "FREDERICK COUNTY",
      "PROJ_NAME": "FREDERICK COUNTY",
      "PU_SAME_PROJECT_AS_SUBJECT": "",
      "PROJ_MONTHLY_FEE": "63",
      "PROJ_AMENITIES": "",
      "PROJ_SPECIAL_ASSESSMENTS": "",
      "SITE_PROJECT_TYPE": "",
      "SITE_OWNED_IN_COMMON": "",
      "ST_SITSIZE": "6104 sf",
      "MARKETAREA": "Frederick County",
      "SITE_ZONE_COMPLIANCE": "",
      "SITE_HAZARD_ZONE": "",
      "SITE_PRIMARY_ACCESS": "",
      "SiteStreetType": [
        {
          "SITE_STREET_TYPE": "",
          "PU_SITE_STREET_TYPE": "",
          "PU_SITE_STREET_SURFACE": ""
        }
      ],
      "SITE_PROP_RESTRICT": "",
      "SITE_EASEMENT": "",
      "SITE_TOPOGRAPHY": "",
      "SiteDrainage": [
        {
          "SITE_DRAINAGE": ""
        }
      ],
      "SITE_CHARACTERISTICS": "",
      "SITE_INFLUENCE": "",
      "PU_SITE_INFLUENCE": "",
      "PU_SITE_INFLUENCE_WATER_TYPE": "",
      "PU_SITE_INFLUENCE_PRIVATE_ACCESS": "",
      "SITE_ENV_COND": "",
      "SiteViews": [
        {
          "ST_VIEW": "",
          "PU_ST_VIEW": "",
          "PU_ST_RANGE": ""
        }
      ],
      "SITE_BLANK": "",
      "Dwellings": [
        {
          "YR_BLT": "4",
          "PU_YR_BLT": "4",
          "PU_YR_BLT_EST": "",
          "DWELL_DESIGN": "",
          "PU_STRUCTURE_DESIGN": "true",
          "SF_GLA": "",
          "SF_FINISHED_AREA": "",
          "DWELL_TH_BACKTOBACK": "",
          "PU_DWELL_TH_BACKTOBACK": "",
          "DWELL_TH_LOC": "",
          "PU_DWELL_TH_LOC": "",
          "DWELL_CONST_METHOD": "",
          "PU_DWELL_CONST_METHOD": "",
          "DWELL_MANFCT_HOME_WIDTH": "",
          "PU_DWELL_MANFCT_HOME_WIDTH": "",
          "DWELL_STYLE": "",
          "PU_DWELL_STYLE": "",
          "DWELL_TOTAL_VOLUME": "",
          "DWELL_WINDOW_AREA": "",
          "DWELL_FUNC_ISSUES": "",
          "PU_DWELL_FUNC_ISSUES": "",
          "DWELL_DISASTER_MIT": "",
          "PU_DWELL_DISASTER_MIT": "",
          "Heating": [
            {
              "DWELL_HEAT": "",
              "PU_DWELL_HEAT_SYSTEM": "",
              "PU_DWELL_HEAT_DETAIL": ""
            }
          ],
          "Cooling": [
            {
              "DWELL_COOL": "",
              "PU_DWELL_COOL": ""
            }
          ],
          "DWELL_OTHR1_BLANK": "",
          "Units": [
            {
              "UNIT_ID": "",
              "UNITS_IS_AN_ADU": "",
              "LOC_ADU": "",
              "PU_LOC_ADU_TYPE": "",
              "PU_LOC_ADU_DESC": "",
              "FLOOR_NUM": "",
              "CORNER_UNIT": "",
              "LEVELS": "",
              "BEDS": "4",
              "BATHS": "",
              "PU_FULL_BATHS": "3",
              "PU_HALF_BATHS": "0",
              "UNITS_SF_GLA": "",
              "UNITS_SF_GLA_NONSTD": "",
              "UNITS_SF_GLA_UNFIN": "",
              "UNITS_SF_GLA_BELOW_GRD": "",
              "UNITS_SF_GLA_BELOW_GRD_NONSTD": "",
              "UNITS_SF_GLA_BELOW_GRD_UNFIN": "",
              "DISABILITY_FEATURES": "",
              "UNITS_BLANK": ""
            }
          ]
        }
      ],
      "WaterFrontages": [
        {
          "WATER_FRONTAGE": "",
          "PU_WATER_FRONTAGE_TYPE": "",
          "PU_WATER_FRONTAGE_NAME": "",
          "PU_WATER_FRONTAGE_ACCESS_DEPTH": ""
        }
      ],
      "WATER_FRONTAGE_FEAT": "",
      "WATER_RIGHT_BLD": "",
      "WATER_LINEAR_MEASURE": "",
      "GREEN_RENEW_ENERGY_ONENT": "",
      "BuildingCertifications": [
        {
          "GREEN_BUILD_CERT": "",
          "PU_GREEN_BUILD_CERT_DESC": "",
          "PU_GREEN_BUILD_CERT_RATE": ""
        }
      ],
      "EfficiencyRatings": [
        {
          "GREEN_EFF_RATING": "",
          "PU_GREEN_EFF_RATING": "",
          "PU_GREEN_EFF_SCORE": ""
        }
      ],
      "ExteriorQualities": [
        {
          "EXT_QUAL_STRUCTURE_ID": "",
          "EXT_QUAL_QUALITY": "",
          "EXT_QUAL_EXT_WALLS_TRIM": "",
          "EXT_QUAL_FOUNDATION": "",
          "EXT_QUAL_ROOF": "",
          "EXT_QUAL_WINDOWS": "",
          "BLExteriorQualities": [
            {
              "EXT_QUAL_BLANK_LABEL": "",
              "EXT_QUAL_BLANK": ""
            }
          ]
        }
      ],
      "ExteriorConditions": [
        {
          "EXT_COND_CONDITION": "",
          "EXT_COND_WALLS_TRIM": "",
          "EXT_COND_FOUNDATION": "",
          "EXT_COND_ROOF": "",
          "PU_EXT_COND_ROOF_OBSERVABLE": "",
          "PU_EXT_COND_ROOF_STATUS": "",
          "EXT_COND_WINDOWS": "",
          "BLExteriorConditions": [
            {
              "EXT_COND_BLANK_LABEL": "",
              "EXT_COND_BLANK": ""
            }
          ]
        }
      ],
      "InteriorQualities": [
        {
          "INT_QUAL_STRUCTURE_ID": "",
          "INT_QUAL_QUALITY": "",
          "INT_QUAL_KITCHEN": "",
          "INT_QUAL_BATHS": "",
          "INT_QUAL_FLOORS": "",
          "INT_QUAL_WALLS_CEILINGS": "",
          "BLInteriorQualities": [
            {
              "INT_QUAL_BLANK_LABEL": "",
              "INT_QUAL_BLANK": ""
            }
          ]
        }
      ],
      "InteriorConditions": [
        {
          "INT_COND_CONDITION": "",
          "INT_COND_KITCHEN": "",
          "INT_COND_BATHS": "",
          "INT_COND_FLOORS": "",
          "INT_COND_WALLS_CEILINGS": "",
          "BLInteriorConditions": [
            {
              "INT_COND_BLANK_LABEL": "",
              "INT_COND_BLANK": ""
            }
          ]
        }
      ],
      "ADUQualities": [
        {
          "ADU_LOCATION": "",
          "ADU_QUAL_QUALITY": "",
          "ADU_QUAL_KITCHEN": "",
          "INT_ADU_COND_BATHS": "",
          "ADU_QUAL_FLOORS": "",
          "ADU_QUAL_WALLS_CEILINGS": "",
          "BLADUQualities": [
            {
              "ADU_QUAL_BLANK_LABEL": "",
              "ADU_QUAL_BLANK": ""
            }
          ]
        }
      ],
      "ADUConditions": [
        {
          "ADU_COND_CONDITION": "",
          "ADU_COND_KITCHEN": "",
          "ADU_COND_BATHS": "",
          "ADU_COND_FLOORS": "",
          "ADU_COND_WALLS_CEILINGS": "",
          "BLADUConditions": [
            {
              "ADU_COND_BLANK_LABEL": "",
              "ADU_COND_BLANK": ""
            }
          ]
        }
      ],
      "QUALITY": "",
      "CONDITION": "",
      "Amenities": [
        {
          "OutdoorAccessories": [
            {
              "AMENITY_OUTDOOR_ACCESSORIES": "",
              "PU_AMENITY_OUTDOOR_ACCESSORIES": "",
              "PU_AMENITY_OUTDOOR_ACCESSORIES_NUM": ""
            }
          ],
          "OutdoorLiving": [
            {
              "AMENITY_OUTDOOR_LIVING": "",
              "PU_AMENITY_OUTDOOR_LIVING": ""
            }
          ],
          "WaterFeatures": [
            {
              "AMENITY_WATER_FEATURES": "",
              "PU_AMENITY_WATER_FEATURE": "",
              "PU_AMENITY_WATER_FEATURE_DETAIL": "",
              "PU_AMENITY_WATER_FEATURE_NUM": ""
            }
          ],
          "WholeHome": [
            {
              "AMENITY_WHOLE_HOME": "",
              "PU_AMENITY_WHOLE_HOME": "",
              "PU_AMENITY_WHOLE_HOME_NUM": ""
            }
          ],
          "Miscellaneous": [
            {
              "AMENITY_MISC": "",
              "PU_AMENITY_MISC": "",
              "PU_AMENITY_MISC_NUM": ""
            }
          ]
        }
      ],
      "VehicleStorage": [
        {
          "VEHICLE_STORAGE": "",
          "PU_VEHICLE_STORAGE": "",
          "PU_VEHICLE_STORAGE_SPACES": "",
          "PU_VEHICLE_STORAGE_DETAILS": "",
          "PU_VEHICLE_STORAGE_SQFT": "",
          "BL_VEHICLE_STORAGE": ""
        }
      ],
      "Outbuildings": [
        {
          "OUTBUILDING_TYPE": "",
          "OUTBUILDING_REAL_PROPERTY": "",
          "OUTBUILDING_BUILDING_AREA": "",
          "OUTBUILDING_FINISHED_AREA": "",
          "OUTBUILDING_UNFINISHED_AREA": "",
          "OUTBUILDING_STRUCTURE_VOLUME": "",
          "OUTBUILDING_BATHS_FULL": "",
          "OUTBUILDING_BATHS_HALF": "",
          "OUTBUILDING_KITCHENS": "",
          "OUTBUILDING_HEATING": "",
          "OUTBUILDING_COOLING": "",
          "OutbuildingUtilities": [
            {
              "OUTBUILDING_UTILITIES": ""
            }
          ],
          "SubjectBLOutbuilding": [
            {
              "BL_OUTBUILDING_BLANK": "",
              "BL_OUTBUILDING": ""
            }
          ]
        }
      ],
      "TransferHistories": [
        {
          "TRANSFER_HISTORY_PRESENT": "",
          "TRANSFER_HISTORY_TERMS": "",
          "TRANSFER_HISTORY_DATE": "",
          "TRANSFER_HISTORY_AMOUNT": "",
          "DFE_TRANSFER_HISTORY_AMOUNT_VALUE": "",
          "DFE_TRANSFER_HISTORY_AMOUNT_REASON": "",
          "TRANSFER_HISTORY_SOURCE": ""
        }
      ]
  },
  {
      "COMP_PHOTO": "",
      "AD_ADDR1": "7 Iroquois Ave",
      "DB_UNITNO": "",
      "DB_CITY": "Milford",
      "DB_STATE": "DE",
      "DB_ZIP": "19963",
      "SC_SOURCE1": "Other",
      "PU_DATA_SOURCE": "Other",
      "PU_DATA_SOURCE_MLS_NUM": "",
      "PU_DATA_SOURCE_ASSESSOR_ID": "",
      "PROX1": "",
      "PU_DISTANCE": "",
      "PU_MILES": "",
      "PU_KILOMETERS": "",
      "PU_DIRECTION": "",
      "LP_PRICE": "475000",
      "LP_STATUS": "",
      "CONT_PRICE": "",
      "PU_CONT_PRICE_KNOWN": "",
      "PU_CONT_PRICE": "",
      "PR_SALEPR1": "",
      "DP_FINTYP1": "",
      "EX_CONCAMT": "",
      "PU_CONC_PRESENT": "",
      "PU_CONC_AMOUNT": "",
      "TRANSFER_TERMS": "",
      "CONT_DATE": "",
      "PU_CONT_DATE_KNOWN": "",
      "PU_CONT_DATE": "",
      "DT_SALTIM1": "",
      "EX_DOM": "1",
      "SP_TO_LP_RATIO": "",
      "ATT_DET": "false",
      "PROP_APPRAISER_RIGHTS": "Fee Simple",
      "ANNUAL_GRND_RENT": "",
      "NATIVE_AM_LANDS": "",
      "PU_NATIVE_AM_LANDS_YES": "",
      "PU_NATIVE_AM_LANDS_NO": "",
      "PU_NATIVE_AM_LANDS_TYPE": "",
      "ALL_RIGHTS_INCLUDED": "",
      "RIGHTS_NOT_INCLUDED": "",
      "SAME_BLDR_AS_SUBJECT": "",
      "GI_BLANK": "",
      "PU_PROJECT_NAME": "NONE AVAILABLE",
      "PROJ_NAME": "NONE AVAILABLE",
      "PU_SAME_PROJECT_AS_SUBJECT": "",
      "PROJ_MONTHLY_FEE": "12",
      "PROJ_AMENITIES": "",
      "PROJ_SPECIAL_ASSESSMENTS": "",
      "SITE_PROJECT_TYPE": "",
      "SITE_OWNED_IN_COMMON": "",
      "ST_SITSIZE": "10454 sf",
      "MARKETAREA": "Orchard Hill",
      "SITE_ZONE_COMPLIANCE": "",
      "SITE_HAZARD_ZONE": "",
      "SITE_PRIMARY_ACCESS": "",
      "SiteStreetType": [
        {
          "SITE_STREET_TYPE": "",
          "PU_SITE_STREET_TYPE": "",
          "PU_SITE_STREET_SURFACE": ""
        }
      ],
      "SITE_PROP_RESTRICT": "",
      "SITE_EASEMENT": "",
      "SITE_TOPOGRAPHY": "",
      "SiteDrainage": [
        {
          "SITE_DRAINAGE": ""
        }
      ],
      "SITE_CHARACTERISTICS": "",
      "SITE_INFLUENCE": "",
      "PU_SITE_INFLUENCE": "",
      "PU_SITE_INFLUENCE_WATER_TYPE": "",
      "PU_SITE_INFLUENCE_PRIVATE_ACCESS": "",
      "SITE_ENV_COND": "",
      "SiteViews": [
        {
          "ST_VIEW": "",
          "PU_ST_VIEW": "",
          "PU_ST_RANGE": ""
        }
      ],
      "SITE_BLANK": "",
      "Dwellings": [
        {
          "YR_BLT": "20",
          "PU_YR_BLT": "20",
          "PU_YR_BLT_EST": "",
          "DWELL_DESIGN": "",
          "PU_STRUCTURE_DESIGN": "true",
          "SF_GLA": "",
          "SF_FINISHED_AREA": "",
          "DWELL_TH_BACKTOBACK": "",
          "PU_DWELL_TH_BACKTOBACK": "",
          "DWELL_TH_LOC": "",
          "PU_DWELL_TH_LOC": "",
          "DWELL_CONST_METHOD": "",
          "PU_DWELL_CONST_METHOD": "",
          "DWELL_MANFCT_HOME_WIDTH": "",
          "PU_DWELL_MANFCT_HOME_WIDTH": "",
          "DWELL_STYLE": "",
          "PU_DWELL_STYLE": "",
          "DWELL_TOTAL_VOLUME": "",
          "DWELL_WINDOW_AREA": "",
          "DWELL_FUNC_ISSUES": "",
          "PU_DWELL_FUNC_ISSUES": "",
          "DWELL_DISASTER_MIT": "",
          "PU_DWELL_DISASTER_MIT": "",
          "Heating": [
            {
              "DWELL_HEAT": "",
              "PU_DWELL_HEAT_SYSTEM": "",
              "PU_DWELL_HEAT_DETAIL": ""
            }
          ],
          "Cooling": [
            {
              "DWELL_COOL": "",
              "PU_DWELL_COOL": ""
            }
          ],
          "DWELL_OTHR1_BLANK": "",
          "Units": [
            {
              "UNIT_ID": "",
              "UNITS_IS_AN_ADU": "",
              "LOC_ADU": "",
              "PU_LOC_ADU_TYPE": "",
              "PU_LOC_ADU_DESC": "",
              "FLOOR_NUM": "",
              "CORNER_UNIT": "",
              "LEVELS": "",
              "BEDS": "4",
              "BATHS": "",
              "PU_FULL_BATHS": "2",
              "PU_HALF_BATHS": "1",
              "UNITS_SF_GLA": "",
              "UNITS_SF_GLA_NONSTD": "",
              "UNITS_SF_GLA_UNFIN": "",
              "UNITS_SF_GLA_BELOW_GRD": "",
              "UNITS_SF_GLA_BELOW_GRD_NONSTD": "",
              "UNITS_SF_GLA_BELOW_GRD_UNFIN": "",
              "DISABILITY_FEATURES": "",
              "UNITS_BLANK": ""
            }
          ]
        }
      ],
      "WaterFrontages": [
        {
          "WATER_FRONTAGE": "",
          "PU_WATER_FRONTAGE_TYPE": "",
          "PU_WATER_FRONTAGE_NAME": "",
          "PU_WATER_FRONTAGE_ACCESS_DEPTH": ""
        }
      ],
      "WATER_FRONTAGE_FEAT": "",
      "WATER_RIGHT_BLD": "",
      "WATER_LINEAR_MEASURE": "",
      "GREEN_RENEW_ENERGY_ONENT": "",
      "BuildingCertifications": [
        {
          "GREEN_BUILD_CERT": "",
          "PU_GREEN_BUILD_CERT_DESC": "",
          "PU_GREEN_BUILD_CERT_RATE": ""
        }
      ],
      "EfficiencyRatings": [
        {
          "GREEN_EFF_RATING": "",
          "PU_GREEN_EFF_RATING": "",
          "PU_GREEN_EFF_SCORE": ""
        }
      ],
      "ExteriorQualities": [
        {
          "EXT_QUAL_STRUCTURE_ID": "",
          "EXT_QUAL_QUALITY": "",
          "EXT_QUAL_EXT_WALLS_TRIM": "",
          "EXT_QUAL_FOUNDATION": "",
          "EXT_QUAL_ROOF": "",
          "EXT_QUAL_WINDOWS": "",
          "BLExteriorQualities": [
            {
              "EXT_QUAL_BLANK_LABEL": "",
              "EXT_QUAL_BLANK": ""
            }
          ]
        }
      ],
      "ExteriorConditions": [
        {
          "EXT_COND_CONDITION": "",
          "EXT_COND_WALLS_TRIM": "",
          "EXT_COND_FOUNDATION": "",
          "EXT_COND_ROOF": "",
          "PU_EXT_COND_ROOF_OBSERVABLE": "",
          "PU_EXT_COND_ROOF_STATUS": "",
          "EXT_COND_WINDOWS": "",
          "BLExteriorConditions": [
            {
              "EXT_COND_BLANK_LABEL": "",
              "EXT_COND_BLANK": ""
            }
          ]
        }
      ],
      "InteriorQualities": [
        {
          "INT_QUAL_STRUCTURE_ID": "",
          "INT_QUAL_QUALITY": "",
          "INT_QUAL_KITCHEN": "",
          "INT_QUAL_BATHS": "",
          "INT_QUAL_FLOORS": "",
          "INT_QUAL_WALLS_CEILINGS": "",
          "BLInteriorQualities": [
            {
              "INT_QUAL_BLANK_LABEL": "",
              "INT_QUAL_BLANK": ""
            }
          ]
        }
      ],
      "InteriorConditions": [
        {
          "INT_COND_CONDITION": "",
          "INT_COND_KITCHEN": "",
          "INT_COND_BATHS": "",
          "INT_COND_FLOORS": "",
          "INT_COND_WALLS_CEILINGS": "",
          "BLInteriorConditions": [
            {
              "INT_COND_BLANK_LABEL": "",
              "INT_COND_BLANK": ""
            }
          ]
        }
      ],
      "ADUQualities": [
        {
          "ADU_LOCATION": "",
          "ADU_QUAL_QUALITY": "",
          "ADU_QUAL_KITCHEN": "",
          "INT_ADU_COND_BATHS": "",
          "ADU_QUAL_FLOORS": "",
          "ADU_QUAL_WALLS_CEILINGS": "",
          "BLADUQualities": [
            {
              "ADU_QUAL_BLANK_LABEL": "",
              "ADU_QUAL_BLANK": ""
            }
          ]
        }
      ],
      "ADUConditions": [
        {
          "ADU_COND_CONDITION": "",
          "ADU_COND_KITCHEN": "",
          "ADU_COND_BATHS": "",
          "ADU_COND_FLOORS": "",
          "ADU_COND_WALLS_CEILINGS": "",
          "BLADUConditions": [
            {
              "ADU_COND_BLANK_LABEL": "",
              "ADU_COND_BLANK": ""
            }
          ]
        }
      ],
      "QUALITY": "",
      "CONDITION": "",
      "Amenities": [
        {
          "OutdoorAccessories": [
            {
              "AMENITY_OUTDOOR_ACCESSORIES": "",
              "PU_AMENITY_OUTDOOR_ACCESSORIES": "",
              "PU_AMENITY_OUTDOOR_ACCESSORIES_NUM": ""
            }
          ],
          "OutdoorLiving": [
            {
              "AMENITY_OUTDOOR_LIVING": "",
              "PU_AMENITY_OUTDOOR_LIVING": ""
            }
          ],
          "WaterFeatures": [
            {
              "AMENITY_WATER_FEATURES": "",
              "PU_AMENITY_WATER_FEATURE": "",
              "PU_AMENITY_WATER_FEATURE_DETAIL": "",
              "PU_AMENITY_WATER_FEATURE_NUM": ""
            }
          ],
          "WholeHome": [
            {
              "AMENITY_WHOLE_HOME": "",
              "PU_AMENITY_WHOLE_HOME": "",
              "PU_AMENITY_WHOLE_HOME_NUM": ""
            }
          ],
          "Miscellaneous": [
            {
              "AMENITY_MISC": "",
              "PU_AMENITY_MISC": "",
              "PU_AMENITY_MISC_NUM": ""
            }
          ]
        }
      ],
      "VehicleStorage": [
        {
          "VEHICLE_STORAGE": "",
          "PU_VEHICLE_STORAGE": "",
          "PU_VEHICLE_STORAGE_SPACES": "",
          "PU_VEHICLE_STORAGE_DETAILS": "",
          "PU_VEHICLE_STORAGE_SQFT": "",
          "BL_VEHICLE_STORAGE": ""
        }
      ],
      "Outbuildings": [
        {
          "OUTBUILDING_TYPE": "",
          "OUTBUILDING_REAL_PROPERTY": "",
          "OUTBUILDING_BUILDING_AREA": "",
          "OUTBUILDING_FINISHED_AREA": "",
          "OUTBUILDING_UNFINISHED_AREA": "",
          "OUTBUILDING_STRUCTURE_VOLUME": "",
          "OUTBUILDING_BATHS_FULL": "",
          "OUTBUILDING_BATHS_HALF": "",
          "OUTBUILDING_KITCHENS": "",
          "OUTBUILDING_HEATING": "",
          "OUTBUILDING_COOLING": "",
          "OutbuildingUtilities": [
            {
              "OUTBUILDING_UTILITIES": ""
            }
          ],
          "SubjectBLOutbuilding": [
            {
              "BL_OUTBUILDING_BLANK": "",
              "BL_OUTBUILDING": ""
            }
          ]
        }
      ],
      "TransferHistories": [
        {
          "TRANSFER_HISTORY_PRESENT": "",
          "TRANSFER_HISTORY_TERMS": "",
          "TRANSFER_HISTORY_DATE": "",
          "TRANSFER_HISTORY_AMOUNT": "",
          "DFE_TRANSFER_HISTORY_AMOUNT_VALUE": "",
          "DFE_TRANSFER_HISTORY_AMOUNT_REASON": "",
          "TRANSFER_HISTORY_SOURCE": ""
        }
      ]
  },
  {
      "COMP_PHOTO": "",
      "AD_ADDR1": "5712 Haller Pl",
      "DB_UNITNO": "",
      "DB_CITY": "Frederick",
      "DB_STATE": "MD",
      "DB_ZIP": "21704",
      "SC_SOURCE1": "Other",
      "PU_DATA_SOURCE": "Other",
      "PU_DATA_SOURCE_MLS_NUM": "",
      "PU_DATA_SOURCE_ASSESSOR_ID": "",
      "PROX1": "",
      "PU_DISTANCE": "",
      "PU_MILES": "",
      "PU_KILOMETERS": "",
      "PU_DIRECTION": "",
      "LP_PRICE": "796655",
      "LP_STATUS": "",
      "CONT_PRICE": "",
      "PU_CONT_PRICE_KNOWN": "",
      "PU_CONT_PRICE": "",
      "PR_SALEPR1": "",
      "DP_FINTYP1": "",
      "EX_CONCAMT": "",
      "PU_CONC_PRESENT": "",
      "PU_CONC_AMOUNT": "",
      "TRANSFER_TERMS": "",
      "CONT_DATE": "",
      "PU_CONT_DATE_KNOWN": "",
      "PU_CONT_DATE": "",
      "DT_SALTIM1": "",
      "EX_DOM": "1",
      "SP_TO_LP_RATIO": "",
      "ATT_DET": "false",
      "PROP_APPRAISER_RIGHTS": "Fee Simple",
      "ANNUAL_GRND_RENT": "",
      "NATIVE_AM_LANDS": "",
      "PU_NATIVE_AM_LANDS_YES": "",
      "PU_NATIVE_AM_LANDS_NO": "",
      "PU_NATIVE_AM_LANDS_TYPE": "",
      "ALL_RIGHTS_INCLUDED": "",
      "RIGHTS_NOT_INCLUDED": "",
      "SAME_BLDR_AS_SUBJECT": "",
      "GI_BLANK": "",
      "PU_PROJECT_NAME": "FREDERICK COUNTY",
      "PROJ_NAME": "FREDERICK COUNTY",
      "PU_SAME_PROJECT_AS_SUBJECT": "",
      "PROJ_MONTHLY_FEE": "63",
      "PROJ_AMENITIES": "",
      "PROJ_SPECIAL_ASSESSMENTS": "",
      "SITE_PROJECT_TYPE": "",
      "SITE_OWNED_IN_COMMON": "",
      "ST_SITSIZE": "6104 sf",
      "MARKETAREA": "Frederick County",
      "SITE_ZONE_COMPLIANCE": "",
      "SITE_HAZARD_ZONE": "",
      "SITE_PRIMARY_ACCESS": "",
      "SiteStreetType": [
        {
          "SITE_STREET_TYPE": "",
          "PU_SITE_STREET_TYPE": "",
          "PU_SITE_STREET_SURFACE": ""
        }
      ],
      "SITE_PROP_RESTRICT": "",
      "SITE_EASEMENT": "",
      "SITE_TOPOGRAPHY": "",
      "SiteDrainage": [
        {
          "SITE_DRAINAGE": ""
        }
      ],
      "SITE_CHARACTERISTICS": "",
      "SITE_INFLUENCE": "",
      "PU_SITE_INFLUENCE": "",
      "PU_SITE_INFLUENCE_WATER_TYPE": "",
      "PU_SITE_INFLUENCE_PRIVATE_ACCESS": "",
      "SITE_ENV_COND": "",
      "SiteViews": [
        {
          "ST_VIEW": "",
          "PU_ST_VIEW": "",
          "PU_ST_RANGE": ""
        }
      ],
      "SITE_BLANK": "",
      "Dwellings": [
        {
          "YR_BLT": "4",
          "PU_YR_BLT": "4",
          "PU_YR_BLT_EST": "",
          "DWELL_DESIGN": "",
          "PU_STRUCTURE_DESIGN": "true",
          "SF_GLA": "",
          "SF_FINISHED_AREA": "",
          "DWELL_TH_BACKTOBACK": "",
          "PU_DWELL_TH_BACKTOBACK": "",
          "DWELL_TH_LOC": "",
          "PU_DWELL_TH_LOC": "",
          "DWELL_CONST_METHOD": "",
          "PU_DWELL_CONST_METHOD": "",
          "DWELL_MANFCT_HOME_WIDTH": "",
          "PU_DWELL_MANFCT_HOME_WIDTH": "",
          "DWELL_STYLE": "",
          "PU_DWELL_STYLE": "",
          "DWELL_TOTAL_VOLUME": "",
          "DWELL_WINDOW_AREA": "",
          "DWELL_FUNC_ISSUES": "",
          "PU_DWELL_FUNC_ISSUES": "",
          "DWELL_DISASTER_MIT": "",
          "PU_DWELL_DISASTER_MIT": "",
          "Heating": [
            {
              "DWELL_HEAT": "",
              "PU_DWELL_HEAT_SYSTEM": "",
              "PU_DWELL_HEAT_DETAIL": ""
            }
          ],
          "Cooling": [
            {
              "DWELL_COOL": "",
              "PU_DWELL_COOL": ""
            }
          ],
          "DWELL_OTHR1_BLANK": "",
          "Units": [
            {
              "UNIT_ID": "",
              "UNITS_IS_AN_ADU": "",
              "LOC_ADU": "",
              "PU_LOC_ADU_TYPE": "",
              "PU_LOC_ADU_DESC": "",
              "FLOOR_NUM": "",
              "CORNER_UNIT": "",
              "LEVELS": "",
              "BEDS": "5",
              "BATHS": "",
              "PU_FULL_BATHS": "4",
              "PU_HALF_BATHS": "0",
              "UNITS_SF_GLA": "",
              "UNITS_SF_GLA_NONSTD": "",
              "UNITS_SF_GLA_UNFIN": "",
              "UNITS_SF_GLA_BELOW_GRD": "",
              "UNITS_SF_GLA_BELOW_GRD_NONSTD": "",
              "UNITS_SF_GLA_BELOW_GRD_UNFIN": "",
              "DISABILITY_FEATURES": "",
              "UNITS_BLANK": ""
            }
          ]
        }
      ],
      "WaterFrontages": [
        {
          "WATER_FRONTAGE": "",
          "PU_WATER_FRONTAGE_TYPE": "",
          "PU_WATER_FRONTAGE_NAME": "",
          "PU_WATER_FRONTAGE_ACCESS_DEPTH": ""
        }
      ],
      "WATER_FRONTAGE_FEAT": "",
      "WATER_RIGHT_BLD": "",
      "WATER_LINEAR_MEASURE": "",
      "GREEN_RENEW_ENERGY_ONENT": "",
      "BuildingCertifications": [
        {
          "GREEN_BUILD_CERT": "",
          "PU_GREEN_BUILD_CERT_DESC": "",
          "PU_GREEN_BUILD_CERT_RATE": ""
        }
      ],
      "EfficiencyRatings": [
        {
          "GREEN_EFF_RATING": "",
          "PU_GREEN_EFF_RATING": "",
          "PU_GREEN_EFF_SCORE": ""
        }
      ],
      "ExteriorQualities": [
        {
          "EXT_QUAL_STRUCTURE_ID": "",
          "EXT_QUAL_QUALITY": "",
          "EXT_QUAL_EXT_WALLS_TRIM": "",
          "EXT_QUAL_FOUNDATION": "",
          "EXT_QUAL_ROOF": "",
          "EXT_QUAL_WINDOWS": "",
          "BLExteriorQualities": [
            {
              "EXT_QUAL_BLANK_LABEL": "",
              "EXT_QUAL_BLANK": ""
            }
          ]
        }
      ],
      "ExteriorConditions": [
        {
          "EXT_COND_CONDITION": "",
          "EXT_COND_WALLS_TRIM": "",
          "EXT_COND_FOUNDATION": "",
          "EXT_COND_ROOF": "",
          "PU_EXT_COND_ROOF_OBSERVABLE": "",
          "PU_EXT_COND_ROOF_STATUS": "",
          "EXT_COND_WINDOWS": "",
          "BLExteriorConditions": [
            {
              "EXT_COND_BLANK_LABEL": "",
              "EXT_COND_BLANK": ""
            }
          ]
        }
      ],
      "InteriorQualities": [
        {
          "INT_QUAL_STRUCTURE_ID": "",
          "INT_QUAL_QUALITY": "",
          "INT_QUAL_KITCHEN": "",
          "INT_QUAL_BATHS": "",
          "INT_QUAL_FLOORS": "",
          "INT_QUAL_WALLS_CEILINGS": "",
          "BLInteriorQualities": [
            {
              "INT_QUAL_BLANK_LABEL": "",
              "INT_QUAL_BLANK": ""
            }
          ]
        }
      ],
      "InteriorConditions": [
        {
          "INT_COND_CONDITION": "",
          "INT_COND_KITCHEN": "",
          "INT_COND_BATHS": "",
          "INT_COND_FLOORS": "",
          "INT_COND_WALLS_CEILINGS": "",
          "BLInteriorConditions": [
            {
              "INT_COND_BLANK_LABEL": "",
              "INT_COND_BLANK": ""
            }
          ]
        }
      ],
      "ADUQualities": [
        {
          "ADU_LOCATION": "",
          "ADU_QUAL_QUALITY": "",
          "ADU_QUAL_KITCHEN": "",
          "INT_ADU_COND_BATHS": "",
          "ADU_QUAL_FLOORS": "",
          "ADU_QUAL_WALLS_CEILINGS": "",
          "BLADUQualities": [
            {
              "ADU_QUAL_BLANK_LABEL": "",
              "ADU_QUAL_BLANK": ""
            }
          ]
        }
      ],
      "ADUConditions": [
        {
          "ADU_COND_CONDITION": "",
          "ADU_COND_KITCHEN": "",
          "ADU_COND_BATHS": "",
          "ADU_COND_FLOORS": "",
          "ADU_COND_WALLS_CEILINGS": "",
          "BLADUConditions": [
            {
              "ADU_COND_BLANK_LABEL": "",
              "ADU_COND_BLANK": ""
            }
          ]
        }
      ],
      "QUALITY": "",
      "CONDITION": "",
      "Amenities": [
        {
          "OutdoorAccessories": [
            {
              "AMENITY_OUTDOOR_ACCESSORIES": "",
              "PU_AMENITY_OUTDOOR_ACCESSORIES": "",
              "PU_AMENITY_OUTDOOR_ACCESSORIES_NUM": ""
            }
          ],
          "OutdoorLiving": [
            {
              "AMENITY_OUTDOOR_LIVING": "",
              "PU_AMENITY_OUTDOOR_LIVING": ""
            }
          ],
          "WaterFeatures": [
            {
              "AMENITY_WATER_FEATURES": "",
              "PU_AMENITY_WATER_FEATURE": "",
              "PU_AMENITY_WATER_FEATURE_DETAIL": "",
              "PU_AMENITY_WATER_FEATURE_NUM": ""
            }
          ],
          "WholeHome": [
            {
              "AMENITY_WHOLE_HOME": "",
              "PU_AMENITY_WHOLE_HOME": "",
              "PU_AMENITY_WHOLE_HOME_NUM": ""
            }
          ],
          "Miscellaneous": [
            {
              "AMENITY_MISC": "",
              "PU_AMENITY_MISC": "",
              "PU_AMENITY_MISC_NUM": ""
            }
          ]
        }
      ],
      "VehicleStorage": [
        {
          "VEHICLE_STORAGE": "",
          "PU_VEHICLE_STORAGE": "",
          "PU_VEHICLE_STORAGE_SPACES": "",
          "PU_VEHICLE_STORAGE_DETAILS": "",
          "PU_VEHICLE_STORAGE_SQFT": "",
          "BL_VEHICLE_STORAGE": ""
        }
      ],
      "Outbuildings": [
        {
          "OUTBUILDING_TYPE": "",
          "OUTBUILDING_REAL_PROPERTY": "",
          "OUTBUILDING_BUILDING_AREA": "",
          "OUTBUILDING_FINISHED_AREA": "",
          "OUTBUILDING_UNFINISHED_AREA": "",
          "OUTBUILDING_STRUCTURE_VOLUME": "",
          "OUTBUILDING_BATHS_FULL": "",
          "OUTBUILDING_BATHS_HALF": "",
          "OUTBUILDING_KITCHENS": "",
          "OUTBUILDING_HEATING": "",
          "OUTBUILDING_COOLING": "",
          "OutbuildingUtilities": [
            {
              "OUTBUILDING_UTILITIES": ""
            }
          ],
          "SubjectBLOutbuilding": [
            {
              "BL_OUTBUILDING_BLANK": "",
              "BL_OUTBUILDING": ""
            }
          ]
        }
      ],
      "TransferHistories": [
        {
          "TRANSFER_HISTORY_PRESENT": "",
          "TRANSFER_HISTORY_TERMS": "",
          "TRANSFER_HISTORY_DATE": "",
          "TRANSFER_HISTORY_AMOUNT": "",
          "DFE_TRANSFER_HISTORY_AMOUNT_VALUE": "",
          "DFE_TRANSFER_HISTORY_AMOUNT_REASON": "",
          "TRANSFER_HISTORY_SOURCE": ""
        }
      ]
  },
  {
      "COMP_PHOTO": "",
      "AD_ADDR1": "6128 Dock St",
      "DB_UNITNO": "",
      "DB_CITY": "Frederick",
      "DB_STATE": "MD",
      "DB_ZIP": "21703",
      "SC_SOURCE1": "Other",
      "PU_DATA_SOURCE": "Other",
      "PU_DATA_SOURCE_MLS_NUM": "",
      "PU_DATA_SOURCE_ASSESSOR_ID": "",
      "PROX1": "",
      "PU_DISTANCE": "",
      "PU_MILES": "",
      "PU_KILOMETERS": "",
      "PU_DIRECTION": "",
      "LP_PRICE": "484990",
      "LP_STATUS": "",
      "CONT_PRICE": "",
      "PU_CONT_PRICE_KNOWN": "",
      "PU_CONT_PRICE": "",
      "PR_SALEPR1": "",
      "DP_FINTYP1": "",
      "EX_CONCAMT": "",
      "PU_CONC_PRESENT": "",
      "PU_CONC_AMOUNT": "",
      "TRANSFER_TERMS": "",
      "CONT_DATE": "",
      "PU_CONT_DATE_KNOWN": "",
      "PU_CONT_DATE": "",
      "DT_SALTIM1": "",
      "EX_DOM": "85",
      "SP_TO_LP_RATIO": "",
      "ATT_DET": "true",
      "PROP_APPRAISER_RIGHTS": "Fee Simple",
      "ANNUAL_GRND_RENT": "",
      "NATIVE_AM_LANDS": "",
      "PU_NATIVE_AM_LANDS_YES": "",
      "PU_NATIVE_AM_LANDS_NO": "",
      "PU_NATIVE_AM_LANDS_TYPE": "",
      "ALL_RIGHTS_INCLUDED": "",
      "RIGHTS_NOT_INCLUDED": "",
      "SAME_BLDR_AS_SUBJECT": "",
      "GI_BLANK": "",
      "PU_PROJECT_NAME": "HARRINGTON TERRACE",
      "PROJ_NAME": "HARRINGTON TERRACE",
      "PU_SAME_PROJECT_AS_SUBJECT": "",
      "PROJ_MONTHLY_FEE": "77",
      "PROJ_AMENITIES": "",
      "PROJ_SPECIAL_ASSESSMENTS": "",
      "SITE_PROJECT_TYPE": "",
      "SITE_OWNED_IN_COMMON": "",
      "ST_SITSIZE": "3049 sf",
      "MARKETAREA": "Harrington Terrace",
      "SITE_ZONE_COMPLIANCE": "",
      "SITE_HAZARD_ZONE": "",
      "SITE_PRIMARY_ACCESS": "",
      "SiteStreetType": [
        {
          "SITE_STREET_TYPE": "",
          "PU_SITE_STREET_TYPE": "",
          "PU_SITE_STREET_SURFACE": ""
        }
      ],
      "SITE_PROP_RESTRICT": "",
      "SITE_EASEMENT": "",
      "SITE_TOPOGRAPHY": "",
      "SiteDrainage": [
        {
          "SITE_DRAINAGE": ""
        }
      ],
      "SITE_CHARACTERISTICS": "",
      "SITE_INFLUENCE": "",
      "PU_SITE_INFLUENCE": "",
      "PU_SITE_INFLUENCE_WATER_TYPE": "",
      "PU_SITE_INFLUENCE_PRIVATE_ACCESS": "",
      "SITE_ENV_COND": "",
      "SiteViews": [
        {
          "ST_VIEW": "",
          "PU_ST_VIEW": "",
          "PU_ST_RANGE": ""
        }
      ],
      "SITE_BLANK": "",
      "Dwellings": [
        {
          "YR_BLT": "4",
          "PU_YR_BLT": "4",
          "PU_YR_BLT_EST": "",
          "DWELL_DESIGN": "",
          "PU_STRUCTURE_DESIGN": "false",
          "SF_GLA": "",
          "SF_FINISHED_AREA": "",
          "DWELL_TH_BACKTOBACK": "",
          "PU_DWELL_TH_BACKTOBACK": "",
          "DWELL_TH_LOC": "",
          "PU_DWELL_TH_LOC": "",
          "DWELL_CONST_METHOD": "",
          "PU_DWELL_CONST_METHOD": "",
          "DWELL_MANFCT_HOME_WIDTH": "",
          "PU_DWELL_MANFCT_HOME_WIDTH": "",
          "DWELL_STYLE": "",
          "PU_DWELL_STYLE": "",
          "DWELL_TOTAL_VOLUME": "",
          "DWELL_WINDOW_AREA": "",
          "DWELL_FUNC_ISSUES": "",
          "PU_DWELL_FUNC_ISSUES": "",
          "DWELL_DISASTER_MIT": "",
          "PU_DWELL_DISASTER_MIT": "",
          "Heating": [
            {
              "DWELL_HEAT": "",
              "PU_DWELL_HEAT_SYSTEM": "",
              "PU_DWELL_HEAT_DETAIL": ""
            }
          ],
          "Cooling": [
            {
              "DWELL_COOL": "",
              "PU_DWELL_COOL": ""
            }
          ],
          "DWELL_OTHR1_BLANK": "",
          "Units": [
            {
              "UNIT_ID": "",
              "UNITS_IS_AN_ADU": "",
              "LOC_ADU": "",
              "PU_LOC_ADU_TYPE": "",
              "PU_LOC_ADU_DESC": "",
              "FLOOR_NUM": "",
              "CORNER_UNIT": "",
              "LEVELS": "",
              "BEDS": "3",
              "BATHS": "",
              "PU_FULL_BATHS": "2",
              "PU_HALF_BATHS": "1",
              "UNITS_SF_GLA": "",
              "UNITS_SF_GLA_NONSTD": "",
              "UNITS_SF_GLA_UNFIN": "",
              "UNITS_SF_GLA_BELOW_GRD": "",
              "UNITS_SF_GLA_BELOW_GRD_NONSTD": "",
              "UNITS_SF_GLA_BELOW_GRD_UNFIN": "",
              "DISABILITY_FEATURES": "",
              "UNITS_BLANK": ""
            }
          ]
        }
      ],
      "WaterFrontages": [
        {
          "WATER_FRONTAGE": "",
          "PU_WATER_FRONTAGE_TYPE": "",
          "PU_WATER_FRONTAGE_NAME": "",
          "PU_WATER_FRONTAGE_ACCESS_DEPTH": ""
        }
      ],
      "WATER_FRONTAGE_FEAT": "",
      "WATER_RIGHT_BLD": "",
      "WATER_LINEAR_MEASURE": "",
      "GREEN_RENEW_ENERGY_ONENT": "",
      "BuildingCertifications": [
        {
          "GREEN_BUILD_CERT": "",
          "PU_GREEN_BUILD_CERT_DESC": "",
          "PU_GREEN_BUILD_CERT_RATE": ""
        }
      ],
      "EfficiencyRatings": [
        {
          "GREEN_EFF_RATING": "",
          "PU_GREEN_EFF_RATING": "",
          "PU_GREEN_EFF_SCORE": ""
        }
      ],
      "ExteriorQualities": [
        {
          "EXT_QUAL_STRUCTURE_ID": "",
          "EXT_QUAL_QUALITY": "",
          "EXT_QUAL_EXT_WALLS_TRIM": "",
          "EXT_QUAL_FOUNDATION": "",
          "EXT_QUAL_ROOF": "",
          "EXT_QUAL_WINDOWS": "",
          "BLExteriorQualities": [
            {
              "EXT_QUAL_BLANK_LABEL": "",
              "EXT_QUAL_BLANK": ""
            }
          ]
        }
      ],
      "ExteriorConditions": [
        {
          "EXT_COND_CONDITION": "",
          "EXT_COND_WALLS_TRIM": "",
          "EXT_COND_FOUNDATION": "",
          "EXT_COND_ROOF": "",
          "PU_EXT_COND_ROOF_OBSERVABLE": "",
          "PU_EXT_COND_ROOF_STATUS": "",
          "EXT_COND_WINDOWS": "",
          "BLExteriorConditions": [
            {
              "EXT_COND_BLANK_LABEL": "",
              "EXT_COND_BLANK": ""
            }
          ]
        }
      ],
      "InteriorQualities": [
        {
          "INT_QUAL_STRUCTURE_ID": "",
          "INT_QUAL_QUALITY": "",
          "INT_QUAL_KITCHEN": "",
          "INT_QUAL_BATHS": "",
          "INT_QUAL_FLOORS": "",
          "INT_QUAL_WALLS_CEILINGS": "",
          "BLInteriorQualities": [
            {
              "INT_QUAL_BLANK_LABEL": "",
              "INT_QUAL_BLANK": ""
            }
          ]
        }
      ],
      "InteriorConditions": [
        {
          "INT_COND_CONDITION": "",
          "INT_COND_KITCHEN": "",
          "INT_COND_BATHS": "",
          "INT_COND_FLOORS": "",
          "INT_COND_WALLS_CEILINGS": "",
          "BLInteriorConditions": [
            {
              "INT_COND_BLANK_LABEL": "",
              "INT_COND_BLANK": ""
            }
          ]
        }
      ],
      "ADUQualities": [
        {
          "ADU_LOCATION": "",
          "ADU_QUAL_QUALITY": "",
          "ADU_QUAL_KITCHEN": "",
          "INT_ADU_COND_BATHS": "",
          "ADU_QUAL_FLOORS": "",
          "ADU_QUAL_WALLS_CEILINGS": "",
          "BLADUQualities": [
            {
              "ADU_QUAL_BLANK_LABEL": "",
              "ADU_QUAL_BLANK": ""
            }
          ]
        }
      ],
      "ADUConditions": [
        {
          "ADU_COND_CONDITION": "",
          "ADU_COND_KITCHEN": "",
          "ADU_COND_BATHS": "",
          "ADU_COND_FLOORS": "",
          "ADU_COND_WALLS_CEILINGS": "",
          "BLADUConditions": [
            {
              "ADU_COND_BLANK_LABEL": "",
              "ADU_COND_BLANK": ""
            }
          ]
        }
      ],
      "QUALITY": "",
      "CONDITION": "",
      "Amenities": [
        {
          "OutdoorAccessories": [
            {
              "AMENITY_OUTDOOR_ACCESSORIES": "",
              "PU_AMENITY_OUTDOOR_ACCESSORIES": "",
              "PU_AMENITY_OUTDOOR_ACCESSORIES_NUM": ""
            }
          ],
          "OutdoorLiving": [
            {
              "AMENITY_OUTDOOR_LIVING": "",
              "PU_AMENITY_OUTDOOR_LIVING": ""
            }
          ],
          "WaterFeatures": [
            {
              "AMENITY_WATER_FEATURES": "",
              "PU_AMENITY_WATER_FEATURE": "",
              "PU_AMENITY_WATER_FEATURE_DETAIL": "",
              "PU_AMENITY_WATER_FEATURE_NUM": ""
            }
          ],
          "WholeHome": [
            {
              "AMENITY_WHOLE_HOME": "",
              "PU_AMENITY_WHOLE_HOME": "",
              "PU_AMENITY_WHOLE_HOME_NUM": ""
            }
          ],
          "Miscellaneous": [
            {
              "AMENITY_MISC": "",
              "PU_AMENITY_MISC": "",
              "PU_AMENITY_MISC_NUM": ""
            }
          ]
        }
      ],
      "VehicleStorage": [
        {
          "VEHICLE_STORAGE": "",
          "PU_VEHICLE_STORAGE": "",
          "PU_VEHICLE_STORAGE_SPACES": "",
          "PU_VEHICLE_STORAGE_DETAILS": "",
          "PU_VEHICLE_STORAGE_SQFT": "",
          "BL_VEHICLE_STORAGE": ""
        }
      ],
      "Outbuildings": [
        {
          "OUTBUILDING_TYPE": "",
          "OUTBUILDING_REAL_PROPERTY": "",
          "OUTBUILDING_BUILDING_AREA": "",
          "OUTBUILDING_FINISHED_AREA": "",
          "OUTBUILDING_UNFINISHED_AREA": "",
          "OUTBUILDING_STRUCTURE_VOLUME": "",
          "OUTBUILDING_BATHS_FULL": "",
          "OUTBUILDING_BATHS_HALF": "",
          "OUTBUILDING_KITCHENS": "",
          "OUTBUILDING_HEATING": "",
          "OUTBUILDING_COOLING": "",
          "OutbuildingUtilities": [
            {
              "OUTBUILDING_UTILITIES": ""
            }
          ],
          "SubjectBLOutbuilding": [
            {
              "BL_OUTBUILDING_BLANK": "",
              "BL_OUTBUILDING": ""
            }
          ]
        }
      ],
      "TransferHistories": [
        {
          "TRANSFER_HISTORY_PRESENT": "",
          "TRANSFER_HISTORY_TERMS": "",
          "TRANSFER_HISTORY_DATE": "",
          "TRANSFER_HISTORY_AMOUNT": "",
          "DFE_TRANSFER_HISTORY_AMOUNT_VALUE": "",
          "DFE_TRANSFER_HISTORY_AMOUNT_REASON": "",
          "TRANSFER_HISTORY_SOURCE": ""
        }
      ]
  },
  {
      "COMP_PHOTO": "",
      "AD_ADDR1": "302 Furgeson Ln",
      "DB_UNITNO": "",
      "DB_CITY": "Frederick",
      "DB_STATE": "MD",
      "DB_ZIP": "21702",
      "SC_SOURCE1": "Other",
      "PU_DATA_SOURCE": "Other",
      "PU_DATA_SOURCE_MLS_NUM": "",
      "PU_DATA_SOURCE_ASSESSOR_ID": "",
      "PROX1": "",
      "PU_DISTANCE": "",
      "PU_MILES": "",
      "PU_KILOMETERS": "",
      "PU_DIRECTION": "",
      "LP_PRICE": "425900",
      "LP_STATUS": "",
      "CONT_PRICE": "",
      "PU_CONT_PRICE_KNOWN": "",
      "PU_CONT_PRICE": "",
      "PR_SALEPR1": "",
      "DP_FINTYP1": "",
      "EX_CONCAMT": "",
      "PU_CONC_PRESENT": "",
      "PU_CONC_AMOUNT": "",
      "TRANSFER_TERMS": "",
      "CONT_DATE": "",
      "PU_CONT_DATE_KNOWN": "",
      "PU_CONT_DATE": "",
      "DT_SALTIM1": "",
      "EX_DOM": "1",
      "SP_TO_LP_RATIO": "",
      "ATT_DET": "true",
      "PROP_APPRAISER_RIGHTS": "Fee Simple",
      "ANNUAL_GRND_RENT": "",
      "NATIVE_AM_LANDS": "",
      "PU_NATIVE_AM_LANDS_YES": "",
      "PU_NATIVE_AM_LANDS_NO": "",
      "PU_NATIVE_AM_LANDS_TYPE": "",
      "ALL_RIGHTS_INCLUDED": "",
      "RIGHTS_NOT_INCLUDED": "",
      "SAME_BLDR_AS_SUBJECT": "",
      "GI_BLANK": "",
      "PU_PROJECT_NAME": "GAMBRILL VIEW",
      "PROJ_NAME": "GAMBRILL VIEW",
      "PU_SAME_PROJECT_AS_SUBJECT": "",
      "PROJ_MONTHLY_FEE": "115",
      "PROJ_AMENITIES": "",
      "PROJ_SPECIAL_ASSESSMENTS": "",
      "SITE_PROJECT_TYPE": "",
      "SITE_OWNED_IN_COMMON": "",
      "ST_SITSIZE": "5227 sf",
      "MARKETAREA": "Gambrill View",
      "SITE_ZONE_COMPLIANCE": "",
      "SITE_HAZARD_ZONE": "",
      "SITE_PRIMARY_ACCESS": "",
      "SiteStreetType": [
        {
          "SITE_STREET_TYPE": "",
          "PU_SITE_STREET_TYPE": "",
          "PU_SITE_STREET_SURFACE": ""
        }
      ],
      "SITE_PROP_RESTRICT": "",
      "SITE_EASEMENT": "",
      "SITE_TOPOGRAPHY": "",
      "SiteDrainage": [
        {
          "SITE_DRAINAGE": ""
        }
      ],
      "SITE_CHARACTERISTICS": "",
      "SITE_INFLUENCE": "",
      "PU_SITE_INFLUENCE": "",
      "PU_SITE_INFLUENCE_WATER_TYPE": "",
      "PU_SITE_INFLUENCE_PRIVATE_ACCESS": "",
      "SITE_ENV_COND": "",
      "SiteViews": [
        {
          "ST_VIEW": "",
          "PU_ST_VIEW": "",
          "PU_ST_RANGE": ""
        }
      ],
      "SITE_BLANK": "",
      "Dwellings": [
        {
          "YR_BLT": "3",
          "PU_YR_BLT": "3",
          "PU_YR_BLT_EST": "",
          "DWELL_DESIGN": "",
          "PU_STRUCTURE_DESIGN": "false",
          "SF_GLA": "",
          "SF_FINISHED_AREA": "",
          "DWELL_TH_BACKTOBACK": "",
          "PU_DWELL_TH_BACKTOBACK": "",
          "DWELL_TH_LOC": "",
          "PU_DWELL_TH_LOC": "",
          "DWELL_CONST_METHOD": "",
          "PU_DWELL_CONST_METHOD": "",
          "DWELL_MANFCT_HOME_WIDTH": "",
          "PU_DWELL_MANFCT_HOME_WIDTH": "",
          "DWELL_STYLE": "",
          "PU_DWELL_STYLE": "",
          "DWELL_TOTAL_VOLUME": "",
          "DWELL_WINDOW_AREA": "",
          "DWELL_FUNC_ISSUES": "",
          "PU_DWELL_FUNC_ISSUES": "",
          "DWELL_DISASTER_MIT": "",
          "PU_DWELL_DISASTER_MIT": "",
          "Heating": [
            {
              "DWELL_HEAT": "",
              "PU_DWELL_HEAT_SYSTEM": "",
              "PU_DWELL_HEAT_DETAIL": ""
            }
          ],
          "Cooling": [
            {
              "DWELL_COOL": "",
              "PU_DWELL_COOL": ""
            }
          ],
          "DWELL_OTHR1_BLANK": "",
          "Units": [
            {
              "UNIT_ID": "",
              "UNITS_IS_AN_ADU": "",
              "LOC_ADU": "",
              "PU_LOC_ADU_TYPE": "",
              "PU_LOC_ADU_DESC": "",
              "FLOOR_NUM": "",
              "CORNER_UNIT": "",
              "LEVELS": "",
              "BEDS": "3",
              "BATHS": "",
              "PU_FULL_BATHS": "2",
              "PU_HALF_BATHS": "1",
              "UNITS_SF_GLA": "",
              "UNITS_SF_GLA_NONSTD": "",
              "UNITS_SF_GLA_UNFIN": "",
              "UNITS_SF_GLA_BELOW_GRD": "",
              "UNITS_SF_GLA_BELOW_GRD_NONSTD": "",
              "UNITS_SF_GLA_BELOW_GRD_UNFIN": "",
              "DISABILITY_FEATURES": "",
              "UNITS_BLANK": ""
            }
          ]
        }
      ],
      "WaterFrontages": [
        {
          "WATER_FRONTAGE": "",
          "PU_WATER_FRONTAGE_TYPE": "",
          "PU_WATER_FRONTAGE_NAME": "",
          "PU_WATER_FRONTAGE_ACCESS_DEPTH": ""
        }
      ],
      "WATER_FRONTAGE_FEAT": "",
      "WATER_RIGHT_BLD": "",
      "WATER_LINEAR_MEASURE": "",
      "GREEN_RENEW_ENERGY_ONENT": "",
      "BuildingCertifications": [
        {
          "GREEN_BUILD_CERT": "",
          "PU_GREEN_BUILD_CERT_DESC": "",
          "PU_GREEN_BUILD_CERT_RATE": ""
        }
      ],
      "EfficiencyRatings": [
        {
          "GREEN_EFF_RATING": "",
          "PU_GREEN_EFF_RATING": "",
          "PU_GREEN_EFF_SCORE": ""
        }
      ],
      "ExteriorQualities": [
        {
          "EXT_QUAL_STRUCTURE_ID": "",
          "EXT_QUAL_QUALITY": "",
          "EXT_QUAL_EXT_WALLS_TRIM": "",
          "EXT_QUAL_FOUNDATION": "",
          "EXT_QUAL_ROOF": "",
          "EXT_QUAL_WINDOWS": "",
          "BLExteriorQualities": [
            {
              "EXT_QUAL_BLANK_LABEL": "",
              "EXT_QUAL_BLANK": ""
            }
          ]
        }
      ],
      "ExteriorConditions": [
        {
          "EXT_COND_CONDITION": "",
          "EXT_COND_WALLS_TRIM": "",
          "EXT_COND_FOUNDATION": "",
          "EXT_COND_ROOF": "",
          "PU_EXT_COND_ROOF_OBSERVABLE": "",
          "PU_EXT_COND_ROOF_STATUS": "",
          "EXT_COND_WINDOWS": "",
          "BLExteriorConditions": [
            {
              "EXT_COND_BLANK_LABEL": "",
              "EXT_COND_BLANK": ""
            }
          ]
        }
      ],
      "InteriorQualities": [
        {
          "INT_QUAL_STRUCTURE_ID": "",
          "INT_QUAL_QUALITY": "",
          "INT_QUAL_KITCHEN": "",
          "INT_QUAL_BATHS": "",
          "INT_QUAL_FLOORS": "",
          "INT_QUAL_WALLS_CEILINGS": "",
          "BLInteriorQualities": [
            {
              "INT_QUAL_BLANK_LABEL": "",
              "INT_QUAL_BLANK": ""
            }
          ]
        }
      ],
      "InteriorConditions": [
        {
          "INT_COND_CONDITION": "",
          "INT_COND_KITCHEN": "",
          "INT_COND_BATHS": "",
          "INT_COND_FLOORS": "",
          "INT_COND_WALLS_CEILINGS": "",
          "BLInteriorConditions": [
            {
              "INT_COND_BLANK_LABEL": "",
              "INT_COND_BLANK": ""
            }
          ]
        }
      ],
      "ADUQualities": [
        {
          "ADU_LOCATION": "",
          "ADU_QUAL_QUALITY": "",
          "ADU_QUAL_KITCHEN": "",
          "INT_ADU_COND_BATHS": "",
          "ADU_QUAL_FLOORS": "",
          "ADU_QUAL_WALLS_CEILINGS": "",
          "BLADUQualities": [
            {
              "ADU_QUAL_BLANK_LABEL": "",
              "ADU_QUAL_BLANK": ""
            }
          ]
        }
      ],
      "ADUConditions": [
        {
          "ADU_COND_CONDITION": "",
          "ADU_COND_KITCHEN": "",
          "ADU_COND_BATHS": "",
          "ADU_COND_FLOORS": "",
          "ADU_COND_WALLS_CEILINGS": "",
          "BLADUConditions": [
            {
              "ADU_COND_BLANK_LABEL": "",
              "ADU_COND_BLANK": ""
            }
          ]
        }
      ],
      "QUALITY": "",
      "CONDITION": "",
      "Amenities": [
        {
          "OutdoorAccessories": [
            {
              "AMENITY_OUTDOOR_ACCESSORIES": "",
              "PU_AMENITY_OUTDOOR_ACCESSORIES": "",
              "PU_AMENITY_OUTDOOR_ACCESSORIES_NUM": ""
            }
          ],
          "OutdoorLiving": [
            {
              "AMENITY_OUTDOOR_LIVING": "",
              "PU_AMENITY_OUTDOOR_LIVING": ""
            }
          ],
          "WaterFeatures": [
            {
              "AMENITY_WATER_FEATURES": "",
              "PU_AMENITY_WATER_FEATURE": "",
              "PU_AMENITY_WATER_FEATURE_DETAIL": "",
              "PU_AMENITY_WATER_FEATURE_NUM": ""
            }
          ],
          "WholeHome": [
            {
              "AMENITY_WHOLE_HOME": "",
              "PU_AMENITY_WHOLE_HOME": "",
              "PU_AMENITY_WHOLE_HOME_NUM": ""
            }
          ],
          "Miscellaneous": [
            {
              "AMENITY_MISC": "",
              "PU_AMENITY_MISC": "",
              "PU_AMENITY_MISC_NUM": ""
            }
          ]
        }
      ],
      "VehicleStorage": [
        {
          "VEHICLE_STORAGE": "",
          "PU_VEHICLE_STORAGE": "",
          "PU_VEHICLE_STORAGE_SPACES": "",
          "PU_VEHICLE_STORAGE_DETAILS": "",
          "PU_VEHICLE_STORAGE_SQFT": "",
          "BL_VEHICLE_STORAGE": ""
        }
      ],
      "Outbuildings": [
        {
          "OUTBUILDING_TYPE": "",
          "OUTBUILDING_REAL_PROPERTY": "",
          "OUTBUILDING_BUILDING_AREA": "",
          "OUTBUILDING_FINISHED_AREA": "",
          "OUTBUILDING_UNFINISHED_AREA": "",
          "OUTBUILDING_STRUCTURE_VOLUME": "",
          "OUTBUILDING_BATHS_FULL": "",
          "OUTBUILDING_BATHS_HALF": "",
          "OUTBUILDING_KITCHENS": "",
          "OUTBUILDING_HEATING": "",
          "OUTBUILDING_COOLING": "",
          "OutbuildingUtilities": [
            {
              "OUTBUILDING_UTILITIES": ""
            }
          ],
          "SubjectBLOutbuilding": [
            {
              "BL_OUTBUILDING_BLANK": "",
              "BL_OUTBUILDING": ""
            }
          ]
        }
      ],
      "TransferHistories": [
        {
          "TRANSFER_HISTORY_PRESENT": "",
          "TRANSFER_HISTORY_TERMS": "",
          "TRANSFER_HISTORY_DATE": "",
          "TRANSFER_HISTORY_AMOUNT": "",
          "DFE_TRANSFER_HISTORY_AMOUNT_VALUE": "",
          "DFE_TRANSFER_HISTORY_AMOUNT_REASON": "",
          "TRANSFER_HISTORY_SOURCE": ""
        }
      ]
  },
  {
      "COMP_PHOTO": "",
      "AD_ADDR1": "Tbd Olney Sandy Spring Rd",
      "DB_UNITNO": "HOMESITE 4A",
      "DB_CITY": "Ashton",
      "DB_STATE": "MD",
      "DB_ZIP": "20861",
      "SC_SOURCE1": "Other",
      "PU_DATA_SOURCE": "Other",
      "PU_DATA_SOURCE_MLS_NUM": "",
      "PU_DATA_SOURCE_ASSESSOR_ID": "",
      "PROX1": "",
      "PU_DISTANCE": "",
      "PU_MILES": "",
      "PU_KILOMETERS": "",
      "PU_DIRECTION": "",
      "LP_PRICE": "651065",
      "LP_STATUS": "",
      "CONT_PRICE": "",
      "PU_CONT_PRICE_KNOWN": "",
      "PU_CONT_PRICE": "",
      "PR_SALEPR1": "",
      "DP_FINTYP1": "",
      "EX_CONCAMT": "",
      "PU_CONC_PRESENT": "",
      "PU_CONC_AMOUNT": "",
      "TRANSFER_TERMS": "",
      "CONT_DATE": "",
      "PU_CONT_DATE_KNOWN": "",
      "PU_CONT_DATE": "",
      "DT_SALTIM1": "",
      "EX_DOM": "1",
      "SP_TO_LP_RATIO": "",
      "ATT_DET": "false",
      "PROP_APPRAISER_RIGHTS": "Fee Simple",
      "ANNUAL_GRND_RENT": "",
      "NATIVE_AM_LANDS": "",
      "PU_NATIVE_AM_LANDS_YES": "",
      "PU_NATIVE_AM_LANDS_NO": "",
      "PU_NATIVE_AM_LANDS_TYPE": "",
      "ALL_RIGHTS_INCLUDED": "",
      "RIGHTS_NOT_INCLUDED": "",
      "SAME_BLDR_AS_SUBJECT": "",
      "GI_BLANK": "",
      "PU_PROJECT_NAME": "ASHTON MARKET",
      "PROJ_NAME": "ASHTON MARKET",
      "PU_SAME_PROJECT_AS_SUBJECT": "",
      "PROJ_MONTHLY_FEE": "134",
      "PROJ_AMENITIES": "Playground | Jog | Walk Path | Tot Lots",
      "PROJ_SPECIAL_ASSESSMENTS": "",
      "SITE_PROJECT_TYPE": "",
      "SITE_OWNED_IN_COMMON": "",
      "ST_SITSIZE": "1957 sf",
      "MARKETAREA": "Ashton Market",
      "SITE_ZONE_COMPLIANCE": "",
      "SITE_HAZARD_ZONE": "",
      "SITE_PRIMARY_ACCESS": "",
      "SiteStreetType": [
        {
          "SITE_STREET_TYPE": "",
          "PU_SITE_STREET_TYPE": "",
          "PU_SITE_STREET_SURFACE": ""
        }
      ],
      "SITE_PROP_RESTRICT": "",
      "SITE_EASEMENT": "",
      "SITE_TOPOGRAPHY": "",
      "SiteDrainage": [
        {
          "SITE_DRAINAGE": ""
        }
      ],
      "SITE_CHARACTERISTICS": "",
      "SITE_INFLUENCE": "",
      "PU_SITE_INFLUENCE": "",
      "PU_SITE_INFLUENCE_WATER_TYPE": "",
      "PU_SITE_INFLUENCE_PRIVATE_ACCESS": "",
      "SITE_ENV_COND": "",
      "SiteViews": [
        {
          "ST_VIEW": "",
          "PU_ST_VIEW": "",
          "PU_ST_RANGE": ""
        }
      ],
      "SITE_BLANK": "",
      "Dwellings": [
        {
          "YR_BLT": "4",
          "PU_YR_BLT": "4",
          "PU_YR_BLT_EST": "",
          "DWELL_DESIGN": "",
          "PU_STRUCTURE_DESIGN": "false",
          "SF_GLA": "",
          "SF_FINISHED_AREA": "",
          "DWELL_TH_BACKTOBACK": "",
          "PU_DWELL_TH_BACKTOBACK": "",
          "DWELL_TH_LOC": "",
          "PU_DWELL_TH_LOC": "",
          "DWELL_CONST_METHOD": "",
          "PU_DWELL_CONST_METHOD": "",
          "DWELL_MANFCT_HOME_WIDTH": "",
          "PU_DWELL_MANFCT_HOME_WIDTH": "",
          "DWELL_STYLE": "",
          "PU_DWELL_STYLE": "",
          "DWELL_TOTAL_VOLUME": "",
          "DWELL_WINDOW_AREA": "",
          "DWELL_FUNC_ISSUES": "",
          "PU_DWELL_FUNC_ISSUES": "",
          "DWELL_DISASTER_MIT": "",
          "PU_DWELL_DISASTER_MIT": "",
          "Heating": [
            {
              "DWELL_HEAT": "",
              "PU_DWELL_HEAT_SYSTEM": "",
              "PU_DWELL_HEAT_DETAIL": ""
            }
          ],
          "Cooling": [
            {
              "DWELL_COOL": "",
              "PU_DWELL_COOL": ""
            }
          ],
          "DWELL_OTHR1_BLANK": "",
          "Units": [
            {
              "UNIT_ID": "",
              "UNITS_IS_AN_ADU": "",
              "LOC_ADU": "",
              "PU_LOC_ADU_TYPE": "",
              "PU_LOC_ADU_DESC": "",
              "FLOOR_NUM": "",
              "CORNER_UNIT": "",
              "LEVELS": "",
              "BEDS": "3",
              "BATHS": "",
              "PU_FULL_BATHS": "2",
              "PU_HALF_BATHS": "2",
              "UNITS_SF_GLA": "",
              "UNITS_SF_GLA_NONSTD": "",
              "UNITS_SF_GLA_UNFIN": "",
              "UNITS_SF_GLA_BELOW_GRD": "",
              "UNITS_SF_GLA_BELOW_GRD_NONSTD": "",
              "UNITS_SF_GLA_BELOW_GRD_UNFIN": "",
              "DISABILITY_FEATURES": "",
              "UNITS_BLANK": ""
            }
          ]
        }
      ],
      "WaterFrontages": [
        {
          "WATER_FRONTAGE": "",
          "PU_WATER_FRONTAGE_TYPE": "",
          "PU_WATER_FRONTAGE_NAME": "",
          "PU_WATER_FRONTAGE_ACCESS_DEPTH": ""
        }
      ],
      "WATER_FRONTAGE_FEAT": "",
      "WATER_RIGHT_BLD": "",
      "WATER_LINEAR_MEASURE": "",
      "GREEN_RENEW_ENERGY_ONENT": "",
      "BuildingCertifications": [
        {
          "GREEN_BUILD_CERT": "",
          "PU_GREEN_BUILD_CERT_DESC": "",
          "PU_GREEN_BUILD_CERT_RATE": ""
        }
      ],
      "EfficiencyRatings": [
        {
          "GREEN_EFF_RATING": "",
          "PU_GREEN_EFF_RATING": "",
          "PU_GREEN_EFF_SCORE": ""
        }
      ],
      "ExteriorQualities": [
        {
          "EXT_QUAL_STRUCTURE_ID": "",
          "EXT_QUAL_QUALITY": "",
          "EXT_QUAL_EXT_WALLS_TRIM": "",
          "EXT_QUAL_FOUNDATION": "",
          "EXT_QUAL_ROOF": "",
          "EXT_QUAL_WINDOWS": "",
          "BLExteriorQualities": [
            {
              "EXT_QUAL_BLANK_LABEL": "",
              "EXT_QUAL_BLANK": ""
            }
          ]
        }
      ],
      "ExteriorConditions": [
        {
          "EXT_COND_CONDITION": "",
          "EXT_COND_WALLS_TRIM": "",
          "EXT_COND_FOUNDATION": "",
          "EXT_COND_ROOF": "",
          "PU_EXT_COND_ROOF_OBSERVABLE": "",
          "PU_EXT_COND_ROOF_STATUS": "",
          "EXT_COND_WINDOWS": "",
          "BLExteriorConditions": [
            {
              "EXT_COND_BLANK_LABEL": "",
              "EXT_COND_BLANK": ""
            }
          ]
        }
      ],
      "InteriorQualities": [
        {
          "INT_QUAL_STRUCTURE_ID": "",
          "INT_QUAL_QUALITY": "",
          "INT_QUAL_KITCHEN": "",
          "INT_QUAL_BATHS": "",
          "INT_QUAL_FLOORS": "",
          "INT_QUAL_WALLS_CEILINGS": "",
          "BLInteriorQualities": [
            {
              "INT_QUAL_BLANK_LABEL": "",
              "INT_QUAL_BLANK": ""
            }
          ]
        }
      ],
      "InteriorConditions": [
        {
          "INT_COND_CONDITION": "",
          "INT_COND_KITCHEN": "",
          "INT_COND_BATHS": "",
          "INT_COND_FLOORS": "",
          "INT_COND_WALLS_CEILINGS": "",
          "BLInteriorConditions": [
            {
              "INT_COND_BLANK_LABEL": "",
              "INT_COND_BLANK": ""
            }
          ]
        }
      ],
      "ADUQualities": [
        {
          "ADU_LOCATION": "",
          "ADU_QUAL_QUALITY": "",
          "ADU_QUAL_KITCHEN": "",
          "INT_ADU_COND_BATHS": "",
          "ADU_QUAL_FLOORS": "",
          "ADU_QUAL_WALLS_CEILINGS": "",
          "BLADUQualities": [
            {
              "ADU_QUAL_BLANK_LABEL": "",
              "ADU_QUAL_BLANK": ""
            }
          ]
        }
      ],
      "ADUConditions": [
        {
          "ADU_COND_CONDITION": "",
          "ADU_COND_KITCHEN": "",
          "ADU_COND_BATHS": "",
          "ADU_COND_FLOORS": "",
          "ADU_COND_WALLS_CEILINGS": "",
          "BLADUConditions": [
            {
              "ADU_COND_BLANK_LABEL": "",
              "ADU_COND_BLANK": ""
            }
          ]
        }
      ],
      "QUALITY": "",
      "CONDITION": "",
      "Amenities": [
        {
          "OutdoorAccessories": [
            {
              "AMENITY_OUTDOOR_ACCESSORIES": "",
              "PU_AMENITY_OUTDOOR_ACCESSORIES": "",
              "PU_AMENITY_OUTDOOR_ACCESSORIES_NUM": ""
            }
          ],
          "OutdoorLiving": [
            {
              "AMENITY_OUTDOOR_LIVING": "",
              "PU_AMENITY_OUTDOOR_LIVING": ""
            }
          ],
          "WaterFeatures": [
            {
              "AMENITY_WATER_FEATURES": "",
              "PU_AMENITY_WATER_FEATURE": "",
              "PU_AMENITY_WATER_FEATURE_DETAIL": "",
              "PU_AMENITY_WATER_FEATURE_NUM": ""
            }
          ],
          "WholeHome": [
            {
              "AMENITY_WHOLE_HOME": "",
              "PU_AMENITY_WHOLE_HOME": "",
              "PU_AMENITY_WHOLE_HOME_NUM": ""
            }
          ],
          "Miscellaneous": [
            {
              "AMENITY_MISC": "",
              "PU_AMENITY_MISC": "",
              "PU_AMENITY_MISC_NUM": ""
            }
          ]
        }
      ],
      "VehicleStorage": [
        {
          "VEHICLE_STORAGE": "",
          "PU_VEHICLE_STORAGE": "",
          "PU_VEHICLE_STORAGE_SPACES": "",
          "PU_VEHICLE_STORAGE_DETAILS": "",
          "PU_VEHICLE_STORAGE_SQFT": "",
          "BL_VEHICLE_STORAGE": ""
        }
      ],
      "Outbuildings": [
        {
          "OUTBUILDING_TYPE": "",
          "OUTBUILDING_REAL_PROPERTY": "",
          "OUTBUILDING_BUILDING_AREA": "",
          "OUTBUILDING_FINISHED_AREA": "",
          "OUTBUILDING_UNFINISHED_AREA": "",
          "OUTBUILDING_STRUCTURE_VOLUME": "",
          "OUTBUILDING_BATHS_FULL": "",
          "OUTBUILDING_BATHS_HALF": "",
          "OUTBUILDING_KITCHENS": "",
          "OUTBUILDING_HEATING": "",
          "OUTBUILDING_COOLING": "",
          "OutbuildingUtilities": [
            {
              "OUTBUILDING_UTILITIES": ""
            }
          ],
          "SubjectBLOutbuilding": [
            {
              "BL_OUTBUILDING_BLANK": "",
              "BL_OUTBUILDING": ""
            }
          ]
        }
      ],
      "TransferHistories": [
        {
          "TRANSFER_HISTORY_PRESENT": "",
          "TRANSFER_HISTORY_TERMS": "",
          "TRANSFER_HISTORY_DATE": "",
          "TRANSFER_HISTORY_AMOUNT": "",
          "DFE_TRANSFER_HISTORY_AMOUNT_VALUE": "",
          "DFE_TRANSFER_HISTORY_AMOUNT_REASON": "",
          "TRANSFER_HISTORY_SOURCE": ""
        }
      ]
  },
  {
      "COMP_PHOTO": "",
      "AD_ADDR1": "6009 Calla Pl",
      "DB_UNITNO": "",
      "DB_CITY": "Frederick",
      "DB_STATE": "MD",
      "DB_ZIP": "21703",
      "SC_SOURCE1": "Other",
      "PU_DATA_SOURCE": "Other",
      "PU_DATA_SOURCE_MLS_NUM": "",
      "PU_DATA_SOURCE_ASSESSOR_ID": "",
      "PROX1": "",
      "PU_DISTANCE": "",
      "PU_MILES": "",
      "PU_KILOMETERS": "",
      "PU_DIRECTION": "",
      "LP_PRICE": "519990",
      "LP_STATUS": "",
      "CONT_PRICE": "",
      "PU_CONT_PRICE_KNOWN": "",
      "PU_CONT_PRICE": "",
      "PR_SALEPR1": "",
      "DP_FINTYP1": "",
      "EX_CONCAMT": "",
      "PU_CONC_PRESENT": "",
      "PU_CONC_AMOUNT": "",
      "TRANSFER_TERMS": "",
      "CONT_DATE": "",
      "PU_CONT_DATE_KNOWN": "",
      "PU_CONT_DATE": "",
      "DT_SALTIM1": "",
      "EX_DOM": "88",
      "SP_TO_LP_RATIO": "",
      "ATT_DET": "false",
      "PROP_APPRAISER_RIGHTS": "Fee Simple",
      "ANNUAL_GRND_RENT": "",
      "NATIVE_AM_LANDS": "",
      "PU_NATIVE_AM_LANDS_YES": "",
      "PU_NATIVE_AM_LANDS_NO": "",
      "PU_NATIVE_AM_LANDS_TYPE": "",
      "ALL_RIGHTS_INCLUDED": "",
      "RIGHTS_NOT_INCLUDED": "",
      "SAME_BLDR_AS_SUBJECT": "",
      "GI_BLANK": "",
      "PU_PROJECT_NAME": "HARRINGTON TERRACE",
      "PROJ_NAME": "HARRINGTON TERRACE",
      "PU_SAME_PROJECT_AS_SUBJECT": "",
      "PROJ_MONTHLY_FEE": "77",
      "PROJ_AMENITIES": "",
      "PROJ_SPECIAL_ASSESSMENTS": "",
      "SITE_PROJECT_TYPE": "",
      "SITE_OWNED_IN_COMMON": "",
      "ST_SITSIZE": "3049 sf",
      "MARKETAREA": "Harrington Terrace",
      "SITE_ZONE_COMPLIANCE": "",
      "SITE_HAZARD_ZONE": "",
      "SITE_PRIMARY_ACCESS": "",
      "SiteStreetType": [
        {
          "SITE_STREET_TYPE": "",
          "PU_SITE_STREET_TYPE": "",
          "PU_SITE_STREET_SURFACE": ""
        }
      ],
      "SITE_PROP_RESTRICT": "",
      "SITE_EASEMENT": "",
      "SITE_TOPOGRAPHY": "",
      "SiteDrainage": [
        {
          "SITE_DRAINAGE": ""
        }
      ],
      "SITE_CHARACTERISTICS": "",
      "SITE_INFLUENCE": "",
      "PU_SITE_INFLUENCE": "",
      "PU_SITE_INFLUENCE_WATER_TYPE": "",
      "PU_SITE_INFLUENCE_PRIVATE_ACCESS": "",
      "SITE_ENV_COND": "",
      "SiteViews": [
        {
          "ST_VIEW": "",
          "PU_ST_VIEW": "",
          "PU_ST_RANGE": ""
        }
      ],
      "SITE_BLANK": "",
      "Dwellings": [
        {
          "YR_BLT": "4",
          "PU_YR_BLT": "4",
          "PU_YR_BLT_EST": "",
          "DWELL_DESIGN": "",
          "PU_STRUCTURE_DESIGN": "false",
          "SF_GLA": "",
          "SF_FINISHED_AREA": "",
          "DWELL_TH_BACKTOBACK": "",
          "PU_DWELL_TH_BACKTOBACK": "",
          "DWELL_TH_LOC": "",
          "PU_DWELL_TH_LOC": "",
          "DWELL_CONST_METHOD": "",
          "PU_DWELL_CONST_METHOD": "",
          "DWELL_MANFCT_HOME_WIDTH": "",
          "PU_DWELL_MANFCT_HOME_WIDTH": "",
          "DWELL_STYLE": "",
          "PU_DWELL_STYLE": "",
          "DWELL_TOTAL_VOLUME": "",
          "DWELL_WINDOW_AREA": "",
          "DWELL_FUNC_ISSUES": "",
          "PU_DWELL_FUNC_ISSUES": "",
          "DWELL_DISASTER_MIT": "",
          "PU_DWELL_DISASTER_MIT": "",
          "Heating": [
            {
              "DWELL_HEAT": "",
              "PU_DWELL_HEAT_SYSTEM": "",
              "PU_DWELL_HEAT_DETAIL": ""
            }
          ],
          "Cooling": [
            {
              "DWELL_COOL": "",
              "PU_DWELL_COOL": ""
            }
          ],
          "DWELL_OTHR1_BLANK": "",
          "Units": [
            {
              "UNIT_ID": "",
              "UNITS_IS_AN_ADU": "",
              "LOC_ADU": "",
              "PU_LOC_ADU_TYPE": "",
              "PU_LOC_ADU_DESC": "",
              "FLOOR_NUM": "",
              "CORNER_UNIT": "",
              "LEVELS": "",
              "BEDS": "3",
              "BATHS": "",
              "PU_FULL_BATHS": "2",
              "PU_HALF_BATHS": "1",
              "UNITS_SF_GLA": "",
              "UNITS_SF_GLA_NONSTD": "",
              "UNITS_SF_GLA_UNFIN": "",
              "UNITS_SF_GLA_BELOW_GRD": "",
              "UNITS_SF_GLA_BELOW_GRD_NONSTD": "",
              "UNITS_SF_GLA_BELOW_GRD_UNFIN": "",
              "DISABILITY_FEATURES": "",
              "UNITS_BLANK": ""
            }
          ]
        }
      ],
      "WaterFrontages": [
        {
          "WATER_FRONTAGE": "",
          "PU_WATER_FRONTAGE_TYPE": "",
          "PU_WATER_FRONTAGE_NAME": "",
          "PU_WATER_FRONTAGE_ACCESS_DEPTH": ""
        }
      ],
      "WATER_FRONTAGE_FEAT": "",
      "WATER_RIGHT_BLD": "",
      "WATER_LINEAR_MEASURE": "",
      "GREEN_RENEW_ENERGY_ONENT": "",
      "BuildingCertifications": [
        {
          "GREEN_BUILD_CERT": "",
          "PU_GREEN_BUILD_CERT_DESC": "",
          "PU_GREEN_BUILD_CERT_RATE": ""
        }
      ],
      "EfficiencyRatings": [
        {
          "GREEN_EFF_RATING": "",
          "PU_GREEN_EFF_RATING": "",
          "PU_GREEN_EFF_SCORE": ""
        }
      ],
      "ExteriorQualities": [
        {
          "EXT_QUAL_STRUCTURE_ID": "",
          "EXT_QUAL_QUALITY": "",
          "EXT_QUAL_EXT_WALLS_TRIM": "",
          "EXT_QUAL_FOUNDATION": "",
          "EXT_QUAL_ROOF": "",
          "EXT_QUAL_WINDOWS": "",
          "BLExteriorQualities": [
            {
              "EXT_QUAL_BLANK_LABEL": "",
              "EXT_QUAL_BLANK": ""
            }
          ]
        }
      ],
      "ExteriorConditions": [
        {
          "EXT_COND_CONDITION": "",
          "EXT_COND_WALLS_TRIM": "",
          "EXT_COND_FOUNDATION": "",
          "EXT_COND_ROOF": "",
          "PU_EXT_COND_ROOF_OBSERVABLE": "",
          "PU_EXT_COND_ROOF_STATUS": "",
          "EXT_COND_WINDOWS": "",
          "BLExteriorConditions": [
            {
              "EXT_COND_BLANK_LABEL": "",
              "EXT_COND_BLANK": ""
            }
          ]
        }
      ],
      "InteriorQualities": [
        {
          "INT_QUAL_STRUCTURE_ID": "",
          "INT_QUAL_QUALITY": "",
          "INT_QUAL_KITCHEN": "",
          "INT_QUAL_BATHS": "",
          "INT_QUAL_FLOORS": "",
          "INT_QUAL_WALLS_CEILINGS": "",
          "BLInteriorQualities": [
            {
              "INT_QUAL_BLANK_LABEL": "",
              "INT_QUAL_BLANK": ""
            }
          ]
        }
      ],
      "InteriorConditions": [
        {
          "INT_COND_CONDITION": "",
          "INT_COND_KITCHEN": "",
          "INT_COND_BATHS": "",
          "INT_COND_FLOORS": "",
          "INT_COND_WALLS_CEILINGS": "",
          "BLInteriorConditions": [
            {
              "INT_COND_BLANK_LABEL": "",
              "INT_COND_BLANK": ""
            }
          ]
        }
      ],
      "ADUQualities": [
        {
          "ADU_LOCATION": "",
          "ADU_QUAL_QUALITY": "",
          "ADU_QUAL_KITCHEN": "",
          "INT_ADU_COND_BATHS": "",
          "ADU_QUAL_FLOORS": "",
          "ADU_QUAL_WALLS_CEILINGS": "",
          "BLADUQualities": [
            {
              "ADU_QUAL_BLANK_LABEL": "",
              "ADU_QUAL_BLANK": ""
            }
          ]
        }
      ],
      "ADUConditions": [
        {
          "ADU_COND_CONDITION": "",
          "ADU_COND_KITCHEN": "",
          "ADU_COND_BATHS": "",
          "ADU_COND_FLOORS": "",
          "ADU_COND_WALLS_CEILINGS": "",
          "BLADUConditions": [
            {
              "ADU_COND_BLANK_LABEL": "",
              "ADU_COND_BLANK": ""
            }
          ]
        }
      ],
      "QUALITY": "",
      "CONDITION": "",
      "Amenities": [
        {
          "OutdoorAccessories": [
            {
              "AMENITY_OUTDOOR_ACCESSORIES": "",
              "PU_AMENITY_OUTDOOR_ACCESSORIES": "",
              "PU_AMENITY_OUTDOOR_ACCESSORIES_NUM": ""
            }
          ],
          "OutdoorLiving": [
            {
              "AMENITY_OUTDOOR_LIVING": "",
              "PU_AMENITY_OUTDOOR_LIVING": ""
            }
          ],
          "WaterFeatures": [
            {
              "AMENITY_WATER_FEATURES": "",
              "PU_AMENITY_WATER_FEATURE": "",
              "PU_AMENITY_WATER_FEATURE_DETAIL": "",
              "PU_AMENITY_WATER_FEATURE_NUM": ""
            }
          ],
          "WholeHome": [
            {
              "AMENITY_WHOLE_HOME": "",
              "PU_AMENITY_WHOLE_HOME": "",
              "PU_AMENITY_WHOLE_HOME_NUM": ""
            }
          ],
          "Miscellaneous": [
            {
              "AMENITY_MISC": "",
              "PU_AMENITY_MISC": "",
              "PU_AMENITY_MISC_NUM": ""
            }
          ]
        }
      ],
      "VehicleStorage": [
        {
          "VEHICLE_STORAGE": "",
          "PU_VEHICLE_STORAGE": "",
          "PU_VEHICLE_STORAGE_SPACES": "",
          "PU_VEHICLE_STORAGE_DETAILS": "",
          "PU_VEHICLE_STORAGE_SQFT": "",
          "BL_VEHICLE_STORAGE": ""
        }
      ],
      "Outbuildings": [
        {
          "OUTBUILDING_TYPE": "",
          "OUTBUILDING_REAL_PROPERTY": "",
          "OUTBUILDING_BUILDING_AREA": "",
          "OUTBUILDING_FINISHED_AREA": "",
          "OUTBUILDING_UNFINISHED_AREA": "",
          "OUTBUILDING_STRUCTURE_VOLUME": "",
          "OUTBUILDING_BATHS_FULL": "",
          "OUTBUILDING_BATHS_HALF": "",
          "OUTBUILDING_KITCHENS": "",
          "OUTBUILDING_HEATING": "",
          "OUTBUILDING_COOLING": "",
          "OutbuildingUtilities": [
            {
              "OUTBUILDING_UTILITIES": ""
            }
          ],
          "SubjectBLOutbuilding": [
            {
              "BL_OUTBUILDING_BLANK": "",
              "BL_OUTBUILDING": ""
            }
          ]
        }
      ],
      "TransferHistories": [
        {
          "TRANSFER_HISTORY_PRESENT": "",
          "TRANSFER_HISTORY_TERMS": "",
          "TRANSFER_HISTORY_DATE": "",
          "TRANSFER_HISTORY_AMOUNT": "",
          "DFE_TRANSFER_HISTORY_AMOUNT_VALUE": "",
          "DFE_TRANSFER_HISTORY_AMOUNT_REASON": "",
          "TRANSFER_HISTORY_SOURCE": ""
        }
      ]
  }
 ];
}


